import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { AuthoritySource } from '../v2/services/authorizationModel';

/**
 * RegisteredContractsList
 *
 * Surfaces contracts the connected wallet has registered with GeoLink. The
 * Smart Contracts tab previously only exposed the discovery flow (point at
 * an address → see its functions); this panel closes that gap by listing
 * everything the wallet has actually registered, with WASM hash, protocol
 * classification, function inventory and ownership cues.
 *
 * Endpoints used (all multi-tenant scoped per
 * docs/geolink-multi-tenant-scoping-response.md §3):
 *   GET  /api/contracts?public_key=<wallet>          — list user's contracts
 *   POST /api/contracts/{id}/fetch-wasm              — read WASM hash from chain
 *   GET  /api/contracts/{network}/{address}/classification — protocol detection
 *   DELETE /api/contracts/{id}                       — soft-delete (gated by isOwn)
 *
 * Both the `X-Public-Key` header and the `?public_key=` query param are sent
 * so admin / non-admin scoping is unambiguous.
 */

// ── Palette (matches ExecutionRuleManager / GeoTrust dashboard) ──────────────
const C = {
  text: '#333',
  textMuted: '#555',
  textFaint: '#888',
  border: '#ddd',
  borderFaint: '#eee',
  cardBg: '#f8f9fa',
  panelBg: '#fff',
  gold: '#FFD700',
  goldDeep: '#996600',
  green: '#28a745',
  red: '#dc3545',
  blue: '#1f6feb',
};

interface RegisteredContract {
  id: number;
  contract_address: string;
  contract_name: string;
  description?: string | null;
  network?: 'testnet' | 'mainnet' | string;
  is_active?: boolean;
  /** GeoLink may return either an array of function descriptors or an object
   *  keyed by function name depending on the deploy. We normalise on read. */
  discovered_functions?: Array<{ name?: string; signature?: string; returns?: string }> | Record<string, any> | null;
  function_mappings?: Record<string, any> | null;
  /** Some installs persist a hex-encoded WASM hash on the row; if absent we
   *  expose a "Fetch WASM" button that calls /fetch-wasm to populate it. */
  wasm_hash?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  /** Multi-tenant ownership columns (GeoLink migration 023). */
  created_by_public_key?: string | null;
  owner_public_key?: string | null;
}

/**
 * Slim view of an execution rule — we only need the fields that surface
 * the rule's identity, ownership, and whether it's currently armed. Full
 * rule editing lives in the Execution Rules tab; this panel is read-only.
 *
 * `contract_name`, `contract_address`, `network` are denormalized onto
 * the rule rows by GeoLink (via JOIN at query time). We capture them so
 * the orphan-rule detection in `loadContracts` can rebuild a synthetic
 * `RegisteredContract` row when GeoLink filters the parent contract out
 * of `GET /api/contracts` for being inactive (see the GeoLink hand-off
 * doc `geolink-contracts-list-inactive-filter-2026-05-08.md`).
 */
interface LinkedRule {
  id: number;
  rule_name: string;
  rule_type?: string;
  contract_id: number;
  function_name?: string;
  is_active?: boolean;
  trigger_on?: string;
  created_by_public_key?: string | null;
  owner_public_key?: string | null;
  /** Denormalized columns on the rules JOIN — present when GeoLink
   *  surfaces parent-contract metadata alongside the rule. */
  contract_name?: string | null;
  contract_address?: string | null;
  network?: 'testnet' | 'mainnet' | string | null;
}

interface ClassificationResult {
  classification?: string;
  protocol?: string;
  is_defindex?: boolean;
  details?: Record<string, any>;
  /** Some deploys return the classification at the top-level, others nest it
   *  under `classification`. We capture both shapes. */
  [k: string]: any;
}

// ── Contract migration ───────────────────────────────────────────────────────
// Lets the user re-point a registered contract to a new on-chain address
// (e.g. the Zenex 2026-05-08 testnet redeploy) without having to retire +
// re-create it. Because GeoLink's execution rules reference the parent row
// by `contract_id` (FK), updating the address on that row automatically
// migrates every linked rule to the new contract — no rule edits needed.
//
// Pipeline (all GeoLink REST endpoints already exist):
//   1. POST /api/contracts/discover            → discover functions on new address
//   2. PUT  /api/contracts/:id                 → repoint the row + new functions
//   3. POST /api/contracts/:id/fetch-wasm      → refresh WASM hash
//
// The function-signature diff between old and new is shown in step 2 so
// users can see (a) what changed, and (b) which existing rules will keep
// working vs which call a function that's been renamed/removed and will
// need a follow-up rule edit.

type FunctionDiffKind = 'unchanged' | 'changed' | 'removed' | 'added';

interface FunctionDiffEntry {
  kind: FunctionDiffKind;
  name: string;
  oldSignature?: string;
  newSignature?: string;
}

type MigrationStep =
  | 'input'         // user is entering / confirming the new address
  | 'discovering'   // POST /api/contracts/discover in flight
  | 'review'        // diff computed, awaiting user confirm
  | 'submitting'    // PUT /api/contracts/:id + fetch-wasm in flight
  | 'success';      // all done; auto-collapses after a short delay

interface MigrationState {
  contractId: number;
  newAddress: string;
  step: MigrationStep;
  oldFunctions?: DiscoveredFunction[];
  newFunctions?: DiscoveredFunction[];
  diffs?: FunctionDiffEntry[];
  error?: string;
  /** Set true when the post-PUT verification finds GeoLink accepted
   *  the request but the on-chain `contract_address` is unchanged.
   *  Surfaces the "🔁 Re-register at new address" button on the
   *  review step. Tracked in the doc:
   *  `docs/integrations/ai-agents/geolink-put-contract-address-immutable-2026-05-08.md`. */
  immutabilityDetected?: boolean;
  /** When the re-register fallback succeeds, the newly-registered
   *  contract id (different from `contractId` — the old one). Shown
   *  in the success step so the user can locate the new row. */
  newContractId?: number;
}

/**
 * Static registry of contracts we know have been deprecated and have a
 * canonical replacement. When a registered contract matches, the
 * migration UI surfaces a "Use suggested" shortcut + an explanation of
 * why the migration is needed. Add new entries here as upstream
 * protocols redeploy on testnet/mainnet.
 */
interface DeprecationSuggestion {
  oldAddress: string;
  newAddress: string;
  network: 'testnet' | 'mainnet';
  label: string;
  reason: string;
}

// Both legacy Zenex addresses (pre-2026-05-08 `CCKCMJM4…` and the 2026-05-08
// deploy `CCT2VLC7…`) point at the SAME current target `CDVQTKKG…`, so users
// on either older row migrate straight to current and don't need to hop
// through the intermediate. Source of truth lives in
// `server/v2/lib/zenex.js::TESTNET_DEFAULTS` and is mirrored from
// https://api-testnet.zenex.trade/config.
const ZENEX_CURRENT_TRADING_TESTNET =
  'CDVQTKKGP47YNDKGQBMVPJO33NJTBSU4EFWS63SHZTXV2AYGQ2XFFYNV';

const DEPRECATED_CONTRACT_SUGGESTIONS: ReadonlyArray<DeprecationSuggestion> = [
  {
    oldAddress: 'CCKCMJM4HXJ3EOLDKCAU54PVQDDQUH5HYXA3IF6WG5SUZBBZD75A62NP',
    newAddress: ZENEX_CURRENT_TRADING_TESTNET,
    network:    'testnet',
    label:      'Zenex Trading',
    reason:
      'Zenex has redeployed their testnet trading contract twice since this ' +
      'address was registered (2026-05-08 and 2026-05-27). open_market / ' +
      'close_position have the same ABI on the current contract so existing ' +
      'rules will keep working — but each redeploy migrated the USDC issuer ' +
      'too (current: GDFFGMOE…). You may need to establish a new trustline ' +
      'before your first trade.',
  },
  {
    oldAddress: 'CCT2VLC7TLTQDZ23OM7NEE6BFWJFNYXLOHHEACNGXKKOX2F57OKHV4XM',
    newAddress: ZENEX_CURRENT_TRADING_TESTNET,
    network:    'testnet',
    label:      'Zenex Trading',
    reason:
      'Zenex redeployed their testnet trading contract again on 2026-05-27 ' +
      '(third deploy this cycle). open_market / close_position have the same ' +
      'ABI on the new contract so existing rules will keep working — but ' +
      'Zenex also migrated to a new USDC issuer (GDFFGMOE… instead of ' +
      'GDQ6SJBY…), so users may need to establish a new trustline before ' +
      'their first trade.',
  },
];

function findDeprecationSuggestion(
  address: string,
  network?: string,
): DeprecationSuggestion | null {
  const net = (network || 'testnet').toLowerCase();
  return (
    DEPRECATED_CONTRACT_SUGGESTIONS.find(
      (s) => s.oldAddress === address && s.network === net,
    ) ?? null
  );
}

interface RegisteredContractsListProps {
  walletAddress: string | null;
}

const STELLAR_EXPERT = (network: string, address: string) =>
  `https://stellar.expert/explorer/${network === 'mainnet' ? 'public' : 'testnet'}/contract/${address}`;

const truncate = (addr: string, head = 6, tail = 4) =>
  addr.length > head + tail + 1 ? `${addr.slice(0, head)}…${addr.slice(-tail)}` : addr;

const fmtDate = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

interface DiscoveredFunctionParam {
  name: string;
  type?: string;
  optional?: boolean;
}

interface DiscoveredFunction {
  name: string;
  signature?: string;
  returns?: string;
  parameters?: DiscoveredFunctionParam[];
}

const normaliseFunctions = (
  raw: RegisteredContract['discovered_functions'],
): DiscoveredFunction[] => {
  if (!raw) return [];
  const fromObj = (name: string, meta: any): DiscoveredFunction => ({
    name,
    signature: meta?.signature,
    returns: meta?.returns,
    parameters: Array.isArray(meta?.parameters)
      ? meta.parameters
          .map((p: any) => ({
            name: typeof p?.name === 'string' ? p.name : '',
            type: typeof p?.type === 'string' ? p.type : undefined,
            optional: !!p?.optional,
          }))
          .filter((p: DiscoveredFunctionParam) => p.name)
      : undefined,
  });
  if (Array.isArray(raw)) {
    return raw
      .map((f) => fromObj(f?.name || '', f))
      .filter((f) => f.name);
  }
  return Object.entries(raw).map(([name, meta]) => fromObj(name, meta));
};

/**
 * Compute a per-function diff between the old (currently-registered) and
 * new (just-discovered) function lists. Used in the migration review
 * step to show what changes when the contract address is repointed.
 *
 * Match key is `name` (Soroban functions are uniquely named within a
 * contract). Signature equality is used to distinguish `unchanged` from
 * `changed`. We sort the result so the user sees the "everything fine"
 * group first, then the things that need attention, then informational
 * additions last.
 */
function computeFunctionDiff(
  oldFns: DiscoveredFunction[],
  newFns: DiscoveredFunction[],
): FunctionDiffEntry[] {
  const oldByName = new Map<string, DiscoveredFunction>();
  for (const f of oldFns) if (f.name) oldByName.set(f.name, f);
  const newByName = new Map<string, DiscoveredFunction>();
  for (const f of newFns) if (f.name) newByName.set(f.name, f);

  const entries: FunctionDiffEntry[] = [];

  // Functions that exist on the old side: either unchanged, changed, or removed.
  // (Using `.forEach` rather than `for..of (Map)` so the file compiles
  // under the project's `"target": "es5"` tsconfig without needing
  // `downlevelIteration`.)
  oldByName.forEach((oldFn, name) => {
    const newFn = newByName.get(name);
    if (!newFn) {
      entries.push({ kind: 'removed', name, oldSignature: oldFn.signature || name });
      return;
    }
    const oldSig = oldFn.signature || name;
    const newSig = newFn.signature || name;
    entries.push({
      kind:         oldSig === newSig ? 'unchanged' : 'changed',
      name,
      oldSignature: oldSig,
      newSignature: newSig,
    });
  });

  // Functions that only exist on the new side.
  newByName.forEach((newFn, name) => {
    if (oldByName.has(name)) return;
    entries.push({ kind: 'added', name, newSignature: newFn.signature || name });
  });

  const order: Record<FunctionDiffKind, number> = {
    unchanged: 0,
    changed:   1,
    removed:   2,
    added:     3,
  };
  return entries.sort((a, b) => {
    const k = order[a.kind] - order[b.kind];
    return k !== 0 ? k : a.name.localeCompare(b.name);
  });
}

const diffIcon = (kind: FunctionDiffKind): string => {
  switch (kind) {
    case 'unchanged': return '✓';
    case 'changed':   return '⚠';
    case 'removed':   return '✗';
    case 'added':     return '+';
  }
};

/**
 * Soroban function values come back from the simulator as host objects
 * (Address, U64, Bytes, etc.) which our /test-function proxy serialises
 * to JSON. The shape varies by type — sometimes a primitive, sometimes
 * `{ value: "..." }`, sometimes a nested object. We render whatever we
 * get with a JSON pretty-printer and let the user copy it.
 */
const formatSimulationResult = (result: unknown): string => {
  if (result === undefined || result === null) return 'void';
  if (typeof result === 'string') return result;
  if (typeof result === 'number' || typeof result === 'boolean' || typeof result === 'bigint') {
    return String(result);
  }
  try {
    return JSON.stringify(result, (_k, v) => (typeof v === 'bigint' ? v.toString() : v), 2);
  } catch {
    return String(result);
  }
};

/**
 * Best-effort coercion of a string from a text input into the JSON shape
 * Soroban / GeoLink expects. We try JSON first (so users can paste
 * `["a","b"]` or `{"asset":"native"}`), then fall back to numeric, then
 * the raw string. Saves the user from having to wrap every primitive in
 * quotes.
 */
const coerceParamValue = (raw: string): unknown => {
  const trimmed = raw.trim();
  if (trimmed === '') return '';
  if (trimmed === 'true') return true;
  if (trimmed === 'false') return false;
  if (trimmed === 'null') return null;
  // Try JSON for arrays, objects, quoted strings, numbers
  if (/^[[{"]|^-?\d/.test(trimmed)) {
    try {
      return JSON.parse(trimmed);
    } catch {
      /* fall through to raw string */
    }
  }
  return trimmed;
};

export const RegisteredContractsList: React.FC<RegisteredContractsListProps> = ({ walletAddress }) => {
  // Browser traffic goes through the GeoTrust backend proxy, which injects the
  // server-side GeoLink key. The key must never reach the bundle.
  const apiUrl =
    typeof window !== 'undefined'
      ? '/api/v2/geolink/proxy'
      : (process.env.REACT_APP_GEOLINK_API_URL || 'https://testnet.stellargeolink.com');

  const [contracts, setContracts] = useState<RegisteredContract[]>([]);
  const [rules, setRules] = useState<LinkedRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [wasmHashes, setWasmHashes] = useState<Record<number, string>>({});
  const [classifications, setClassifications] = useState<Record<number, ClassificationResult>>({});
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);

  /**
   * Active migration session, if any. Only one card can be migrating at a
   * time (matches the existing one-card-busy `busyId` ergonomic). The
   * migration UI walks through input → discovering → review → submitting
   * → success and then auto-clears two seconds after success so the user
   * sees the new metadata in the refreshed list.
   */
  const [migration, setMigration] = useState<MigrationState | null>(null);

  /**
   * Whether to render soft-deleted (`is_active=false`) contracts.
   * Default: true — so a user who just hit the trash button can see the
   * row marked "inactive" and click Reactivate. Once they're confident
   * they don't need it back they can flip the toggle off and the list
   * collapses to active contracts only.
   */
  const [showInactive, setShowInactive] = useState<boolean>(true);

  /**
   * Count of synthetic contract rows recovered via orphan-rule detection
   * (rules that reference a contract_id GeoLink didn't return). When
   * non-zero, we render an explanatory banner so the user understands
   * (a) why some rows have empty descriptions / WASM hashes, and (b)
   * that this is a workaround for GeoLink's missing inactive filter.
   * Cleared / refreshed on every loadContracts() call.
   */
  const [orphanCount, setOrphanCount] = useState<number>(0);

  /**
   * Per-function simulation state. Keyed by `${contractId}:${functionName}`
   * so we can keep separate parameter inputs and last-run results for every
   * function on every contract without resetting them when the user
   * collapses/re-expands a card.
   */
  const [simParams, setSimParams] = useState<Record<string, Record<string, string>>>({});
  const [simResults, setSimResults] = useState<
    Record<
      string,
      {
        ok: boolean;
        payload: string;
        ranAt: number;
        /** Decoded return type label from GeoLink (Address, i128, Vec, …).
         *  Sourced from `simulation.return_type` (which prefers the
         *  inferred-from-chain type over the possibly-stale declared
         *  type per the GeoLink decoder spec §10). */
        returnType?: string | null;
      }
    >
  >({});
  const [simOpenKey, setSimOpenKey] = useState<string | null>(null);
  const [simBusyKey, setSimBusyKey] = useState<string | null>(null);

  /**
   * Authenticated fetch against GeoLink. Mirrors ExecutionRuleManager's
   * apiFetch — same multi-tenant header (`X-Public-Key`) and the same 403
   * `not_owner` toast surface.
   *
   * Belt-and-suspenders multi-tenancy: we send the wallet's public key
   * THREE ways on every request:
   *   1. `X-Public-Key` header (canonical per the integration guide §3.2)
   *   2. `?public_key=` query string (some routes consult the query
   *      string instead of the header — e.g. legacy lookup paths in
   *      /test-function and /execute that haven't been wired through)
   *   3. `created_by_public_key` body field for POSTs (priority 3 in the
   *      auth resolution chain)
   * Routes that don't care about scoping ignore the extras; routes that
   * do, find an authoritative wallet identity in whichever channel they
   * happen to read from. This avoids "Contract not found" failures that
   * happen when one specific code path doesn't consult the header.
   *
   * Returns `{ status, data }` so callers can distinguish 404 / 403 /
   * 5xx and include actionable detail in their error UI.
   */
  const apiFetch = useCallback(
    async (path: string, opts: RequestInit = {}): Promise<{ status: number; data: any }> => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...((opts.headers as Record<string, string> | undefined) || {}),
      };
      if (walletAddress) headers['X-Public-Key'] = walletAddress;

      let url = path;
      if (walletAddress && !/[?&]public_key=/.test(url)) {
        url += `${url.includes('?') ? '&' : '?'}public_key=${encodeURIComponent(walletAddress)}`;
      }

      // For POST requests, also inject `created_by_public_key` into the
      // JSON body if the body is a JSON object and the field isn't
      // already set. We don't touch FormData / non-JSON payloads.
      let body = opts.body;
      if (
        walletAddress &&
        opts.method &&
        opts.method.toUpperCase() === 'POST' &&
        typeof body === 'string'
      ) {
        try {
          const parsed = JSON.parse(body);
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            if (parsed.created_by_public_key === undefined) {
              parsed.created_by_public_key = walletAddress;
            }
            if (parsed.user_public_key === undefined) {
              parsed.user_public_key = walletAddress;
            }
            body = JSON.stringify(parsed);
          }
        } catch {
          /* not JSON; leave body untouched */
        }
      }

      const r = await fetch(`${apiUrl}${url}`, { ...opts, headers, body });
      const data = await r.json().catch(() => ({}));
      if (r.status === 403 && (data as any)?.code === 'not_owner') {
        const detail = (data as any).details;
        setError(
          detail
            ? `This contract belongs to another wallet — ${detail}`
            : 'This contract belongs to another wallet.',
        );
      }
      return { status: r.status, data };
    },
    [apiUrl, walletAddress],
  );

  const loadContracts = useCallback(async () => {
    if (!walletAddress) {
      setContracts([]);
      setRules([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // `?public_key=` scopes the result on GeoLink's side per the
      // multi-tenant scoping integration guide §3.1; the X-Public-Key
      // header also goes out via apiFetch so non-admin sessions are
      // pinned regardless of which auth path the server resolves first.
      //
      // We pull contracts and rules in parallel so we can show
      // relational counts ("⚡ 3 rules · 2 active") on each contract
      // card without a second blocking round-trip. Rules failure is
      // non-fatal — the contract list renders without rule chips.
      //
      // Contracts: `?is_active=all` so soft-deleted rows
      // (`is_active=false`) come back. Canonical name agreed with the
      // GeoLink team in their 2026-05-08 fix (see
      // `docs/integrations/ai-agents/geolink-contracts-list-inactive-filter-2026-05-08.md`,
      // GeoLink commit 944ef6f). The response carries
      // `list_is_active_filter: "active" | "inactive" | "all"` so we can
      // verify which mode the server applied.
      //
      // Rules: explicitly fetch both `?is_active=true` and
      // `?is_active=false` and merge by id, instead of trusting
      // `?is_active=all`. GeoLink's 944ef6f fix message only covered
      // `/api/contracts`, not `/api/contracts/rules`, and we observed
      // the rules endpoint silently returning 0 rows after a
      // soft-delete → reactivate cycle (the rules' own `is_active`
      // appears to cascade-flip to `false` on contract delete and
      // doesn't reset on contract reactivate, so a default-active
      // filter hides them entirely). Fetching both states explicitly
      // is deterministic across any GeoLink version and skips the
      // alias guesswork.
      const [contractsResp, rulesActiveResp, rulesInactiveResp] = await Promise.all([
        apiFetch(`/api/contracts?is_active=all`),
        apiFetch(`/api/contracts/rules?is_active=true`)
          .catch(() => ({ status: 0, data: null })),
        apiFetch(`/api/contracts/rules?is_active=false`)
          .catch(() => ({ status: 0, data: null })),
      ]);

      const cData = contractsResp.data;
      const fetchedContracts: RegisteredContract[] =
        cData?.success && Array.isArray(cData.contracts)
          ? (cData.contracts as RegisteredContract[])
          : [];
      if (!cData?.success || !Array.isArray(cData?.contracts)) {
        setError(cData?.error || 'Failed to load contracts');
      }

      // Pull rules out of either `rules` or `execution_rules` (GeoLink
      // builds disagree on the field name). Then merge active +
      // inactive responses, dedup by id, and stash for `rulesByContract`.
      const extractRulesArray = (resp: { data: any }): LinkedRule[] => {
        const d = resp?.data;
        if (d?.success && Array.isArray(d.rules)) return d.rules as LinkedRule[];
        if (d && Array.isArray(d.execution_rules)) return d.execution_rules as LinkedRule[];
        return [];
      };
      const ruleMergeById = new Map<number, LinkedRule>();
      for (const r of extractRulesArray(rulesActiveResp)) {
        if (typeof r.id === 'number') ruleMergeById.set(r.id, r);
      }
      for (const r of extractRulesArray(rulesInactiveResp)) {
        if (typeof r.id === 'number' && !ruleMergeById.has(r.id)) {
          ruleMergeById.set(r.id, r);
        }
      }
      const fetchedRules: LinkedRule[] = Array.from(ruleMergeById.values());
      setRules(fetchedRules);

      // Diagnostic: when both rules calls come back empty *and* the
      // user has registered contracts, log the raw responses so we can
      // see whether the server is filtering rules out for some other
      // reason (auth scope, missing `created_by_public_key`, hard-deleted
      // rows, etc.). This is a one-shot console.log — it won't fire
      // in steady state where the user genuinely has no rules and no
      // contracts.
      if (fetchedRules.length === 0 && fetchedContracts.length > 0) {
      }

      // ── Orphan-rule recovery (defensive fallback) ────────────────────────
      // GeoLink shipped the `?is_active=all` filter in commit 944ef6f
      // (2026-05-08) so soft-deleted contracts now come back through
      // `GET /api/contracts` directly. Under that path the loop below
      // is a no-op: every rule's `contract_id` will resolve in
      // `knownIds` and `orphansById` stays empty.
      //
      // We keep the recovery code in place as a defensive fallback for
      // any deploy environment still on the older GeoLink build (e.g.
      // a self-hosted instance, or a mainnet endpoint that hasn't been
      // updated yet). When the server response is missing inactive
      // rows, we synthesize a `RegisteredContract` row for every
      // `contract_id` referenced by a rule but not in the contracts
      // list, pulling `contract_name`/`contract_address`/`network`
      // from the rule's denormalized JOIN columns. The synthetic row
      // has `is_active: false`, which surfaces the green ♻ Reactivate
      // button on the standard card path.
      //
      // See `docs/integrations/ai-agents/geolink-contracts-list-inactive-filter-2026-05-08.md`
      // for the GeoLink-side spec + resolution.
      const knownIds = new Set(fetchedContracts.map((c) => c.id));
      const orphansById = new Map<number, RegisteredContract>();
      for (const r of fetchedRules) {
        if (typeof r.contract_id !== 'number') continue;
        if (knownIds.has(r.contract_id)) continue;
        if (orphansById.has(r.contract_id)) continue;
        // Build a best-effort synthetic row. Anything we don't have
        // (description, function_mappings, discovered_functions,
        // wasm_hash, created_at) stays null so the existing card
        // renders gracefully — the user can hit "Fetch from network"
        // and "Classify" after reactivation to re-populate.
        orphansById.set(r.contract_id, {
          id:                    r.contract_id,
          contract_address:      r.contract_address || '',
          contract_name:         r.contract_name || `Contract #${r.contract_id}`,
          network:               r.network || 'testnet',
          is_active:             false,
          discovered_functions:  null,
          function_mappings:     null,
          wasm_hash:             null,
          created_at:            null,
          updated_at:            null,
          // Inherit ownership from the rule so isOwn() returns true and
          // the ♻ Reactivate button shows up. If the rule has no owner
          // metadata (legacy rows), fall back to the connected wallet
          // since GeoLink scopes /rules to caller-owned rules anyway.
          created_by_public_key:
            r.created_by_public_key ?? r.owner_public_key ?? walletAddress ?? null,
          owner_public_key:
            r.owner_public_key ?? r.created_by_public_key ?? walletAddress ?? null,
        });
      }

      // Append orphans to the contracts list so they render through the
      // standard card path (with the Reactivate button surfaced via
      // is_active=false). The "Show inactive" toggle controls visibility.
      const orphanList = Array.from(orphansById.values());
      setContracts(orphanList.length > 0 ? [...fetchedContracts, ...orphanList] : fetchedContracts);

      // Stash the orphan count so the UI can render an explanatory
      // banner — without it, users won't know why the soft-deleted
      // contract's address shows up as empty (the rules JOIN sometimes
      // doesn't carry contract_address depending on the GeoLink build).
      setOrphanCount(orphanList.length);
    } catch (e: any) {
      setError(e?.message || 'Failed to load contracts');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, walletAddress]);

  useEffect(() => {
    loadContracts();
  }, [loadContracts]);

  /**
   * Whether this wallet may act on a contract registration.
   *
   * Still creator-based, and deliberately so: a GeoLink contract
   * registration has no on-chain counterpart, so there is no
   * `can_manage_registration` to ask. Nothing in `geo-org-registry` or
   * `geo-spatial-registry` knows these rows exist.
   *
   * That makes this the one surface the authorization refactor did not
   * migrate. It is recorded as `AUTHORITY_SOURCE` below rather than left to
   * look like the others: an unlabelled creator check is indistinguishable
   * from an oversight, and the next person to read this should be able to
   * tell that the gap is known.
   *
   * Absent provenance denies. It is not "unowned, so anyone".
   */
  const isOwn = useCallback(
    (c: RegisteredContract): boolean => {
      if (!walletAddress) return false;
      if (c.created_by_public_key) return c.created_by_public_key === walletAddress;
      if (c.owner_public_key) return c.owner_public_key === walletAddress;
      return false;
    },
    [walletAddress],
  );

  /**
   * Named so the fallback is visible in the UI, matching
   * `authorizationModel.AuthoritySource`. See `isOwn`.
   */
  const AUTHORITY_SOURCE: AuthoritySource = 'creator_legacy';

  /**
   * Notify other parts of the app (mainly the sibling
   * `ExecutionRuleManager` tab in `SmartContractsOverlay`) that a
   * contract row has changed state. Sibling components don't share
   * a store with us so they listen for this window event and
   * re-fetch their own data when it fires.
   *
   * We deliberately keep the event payload minimal — listeners can
   * just re-run their list endpoints. Currently fires on
   * delete / reactivate / migrate-confirm. WASM fetch and
   * classification don't change `is_active` or any field a sibling
   * cares about, so they don't dispatch.
   */
  const broadcastContractsChanged = useCallback(
    (reason: 'delete' | 'reactivate' | 'migrate', contractId: number) => {
      try {
        window.dispatchEvent(
          new CustomEvent('geotrust:contracts-changed', {
            detail: { reason, contractId },
          }),
        );
      } catch {
        /* very old browsers without CustomEvent — ignore */
      }
    },
    [],
  );

  /**
   * Index rules by their `contract_id` so each card can render its
   * relational tail in O(1). We compute this once per `rules` change
   * rather than filtering inside every card render.
   */
  const rulesByContract = useMemo(() => {
    const idx = new Map<number, LinkedRule[]>();
    for (const r of rules) {
      if (typeof r.contract_id !== 'number') continue;
      const list = idx.get(r.contract_id);
      if (list) list.push(r);
      else idx.set(r.contract_id, [r]);
    }
    return idx;
  }, [rules]);

  const handleFetchWasm = useCallback(
    async (c: RegisteredContract) => {
      if (busyId === c.id) return;
      setBusyId(c.id);
      setError(null);
      try {
        const { status, data } = await apiFetch(`/api/contracts/${c.id}/fetch-wasm`, {
          method: 'POST',
          body: JSON.stringify({ network: c.network || 'testnet' }),
        });
        // Different GeoLink builds surface the hash under different keys.
        // Accept any of the common ones so we don't have to rev this if the
        // server moves the field around again.
        const hash =
          data?.wasm_hash ||
          data?.hash ||
          data?.contract?.wasm_hash ||
          data?.metadata?.wasm_hash ||
          null;
        if (hash) {
          setWasmHashes((prev) => ({ ...prev, [c.id]: String(hash) }));
        } else if (!data?.success) {
          setError(`Could not fetch WASM hash (HTTP ${status}): ${data?.error || data?.message || 'unknown'}`);
        } else {
          setError('Server returned no WASM hash field — check the GeoLink response shape.');
        }
      } catch (e: any) {
        setError(e?.message || 'Failed to fetch WASM');
      } finally {
        setBusyId(null);
      }
    },
    [apiFetch, busyId],
  );

  const handleClassify = useCallback(
    async (c: RegisteredContract) => {
      if (busyId === c.id) return;
      const network = c.network || 'testnet';
      setBusyId(c.id);
      setError(null);
      try {
        const { status, data } = await apiFetch(
          `/api/contracts/${encodeURIComponent(network)}/${encodeURIComponent(c.contract_address)}/classification`,
        );
        if (status >= 400) {
          setError(`Classification failed (HTTP ${status}): ${data?.error || data?.message || 'unknown'}`);
          return;
        }
        const cls: ClassificationResult =
          data?.classification && typeof data.classification === 'object'
            ? { ...data.classification, ...data }
            : data;
        setClassifications((prev) => ({ ...prev, [c.id]: cls }));
      } catch (e: any) {
        setError(e?.message || 'Classification failed');
      } finally {
        setBusyId(null);
      }
    },
    [apiFetch, busyId],
  );

  /**
   * Soft-delete a contract. We surface the relational impact up-front:
   *   - Total rules referencing this contract
   *   - How many of those are currently active (will likely stop firing)
   *   - How many belong to other wallets (we can't toggle them off)
   *
   * GeoLink doesn't cascade — the rules row stays in `contract_execution_rules`
   * with the contract_id intact. The user just won't be able to execute
   * those rules until the contract is reactivated. We make that tradeoff
   * explicit instead of silently soft-deleting under their feet.
   */
  const handleDelete = useCallback(
    async (c: RegisteredContract) => {
      if (!isOwn(c)) return;
      const linked = rulesByContract.get(c.id) || [];
      const active = linked.filter((r) => r.is_active !== false);
      const sharedActive = active.filter(
        (r) => r.created_by_public_key && r.created_by_public_key !== walletAddress,
      );

      const warningLines: string[] = [`Deactivate "${c.contract_name}"?`];
      warningLines.push('');
      warningLines.push('This is a soft delete (is_active = false). The contract row stays on GeoLink and can be reactivated later.');

      if (linked.length === 0) {
        warningLines.push('');
        warningLines.push('No execution rules reference this contract — safe to deactivate.');
      } else {
        warningLines.push('');
        warningLines.push(`⚠ ${linked.length} execution rule${linked.length === 1 ? '' : 's'} reference this contract (${active.length} active).`);
        warningLines.push('Active rules will stop firing while the contract is deactivated. Rules are NOT deleted.');
        if (sharedActive.length > 0) {
          warningLines.push('');
          warningLines.push(`Heads up: ${sharedActive.length} active rule${sharedActive.length === 1 ? '' : 's'} belong${sharedActive.length === 1 ? 's' : ''} to other wallets — they'll be affected too.`);
        }
      }

      warningLines.push('');
      warningLines.push('Continue?');

      if (!window.confirm(warningLines.join('\n'))) return;

      setBusyId(c.id);
      setError(null);
      try {
        const { status, data } = await apiFetch(`/api/contracts/${c.id}`, { method: 'DELETE' });
        if (data?.success) {
          await loadContracts();
          broadcastContractsChanged('delete', c.id);
        } else {
          setError(`Delete failed (HTTP ${status}): ${data?.error || data?.message || 'unknown'}`);
        }
      } catch (e: any) {
        setError(e?.message || 'Delete failed');
      } finally {
        setBusyId(null);
      }
    },
    [apiFetch, broadcastContractsChanged, isOwn, loadContracts, rulesByContract, walletAddress],
  );

  /**
   * Reactivate a soft-deleted contract. Mirrors `handleDelete` but flips
   * `is_active` back to true via `PUT /api/contracts/:id`. Only owners
   * can reactivate (server enforces; we also gate the UI on `isOwn`).
   *
   * Why this exists: the trash button is a soft-delete (sets
   * `is_active=false`). Without an inverse action, soft-deleted rows
   * become unrecoverable from the UI — even though the row is still in
   * GeoLink's DB. This handler is the safety net for accidental
   * deletions and for the "I deactivated this last week, want to use
   * it again" workflow.
   */
  const handleReactivate = useCallback(
    async (c: RegisteredContract) => {
      if (!isOwn(c)) return;
      if (busyId === c.id) return;
      setBusyId(c.id);
      setError(null);
      try {
        // GeoLink shipped the PUT-on-inactive fix on 2026-05-08
        // (GEOTRUST_MULTI_TENANT_GUIDE.md §3.3 → "Reactivating a
        // soft-deleted contract via PUT /api/contracts/:id"). Their
        // update lookup no longer requires `is_active = true`, so
        // sending `{ is_active: true }` against a soft-deleted row
        // owned by the caller now flips the column and returns the
        // updated row. A 404 from this endpoint is back to meaning
        // the row genuinely doesn't exist or isn't owned by the
        // caller. See
        // docs/integrations/ai-agents/geolink-put-contracts-inactive-2026-05-08.md
        // for the resolution detail.
        const { status, data } = await apiFetch(`/api/contracts/${c.id}`, {
          method: 'PUT',
          body:   JSON.stringify({ is_active: true }),
        });
        if (status >= 400 || !(data?.success || data?.contract)) {
          setError(
            `Reactivate failed (HTTP ${status}): ${data?.error || data?.message || 'unknown'}`,
          );
          return;
        }
        await loadContracts();
        broadcastContractsChanged('reactivate', c.id);
      } catch (e: any) {
        setError(e?.message || 'Reactivate failed');
      } finally {
        setBusyId(null);
      }
    },
    [apiFetch, broadcastContractsChanged, busyId, isOwn, loadContracts],
  );

  /**
   * Reactivate a single execution rule (sets `is_active=true` via
   * `PUT /api/contracts/rules/:id`). Surfaces on each rule row inside
   * an expanded contract card when the rule is owned by the connected
   * wallet *and* currently inactive.
   *
   * Why this exists: GeoLink's `DELETE /api/contracts/:id` appears to
   * cascade-flip child rules to `is_active=false`, and reactivating
   * the parent contract (`PUT /api/contracts/:id { is_active: true }`)
   * does not propagate back down to the rules. So after a soft-delete
   * → reactivate cycle the contract is live again but its rules are
   * still paused. This handler is the per-rule undo for that
   * cascade. The `geotrust:contracts-changed` broadcast pokes the
   * sibling Execution Rules tab to refresh.
   *
   * Authorization: GeoLink enforces ownership server-side; we also
   * gate the UI on `ruleOwned` so the button doesn't surface on
   * shared rules at all.
   */
  const [busyRuleId, setBusyRuleId] = useState<number | null>(null);
  const handleReactivateRule = useCallback(
    async (rule: LinkedRule) => {
      if (typeof rule.id !== 'number') return;
      if (busyRuleId === rule.id) return;
      setBusyRuleId(rule.id);
      setError(null);
      try {
        const { status, data } = await apiFetch(`/api/contracts/rules/${rule.id}`, {
          method: 'PUT',
          body:   JSON.stringify({ is_active: true }),
        });
        if (status >= 400 || data?.success === false) {
          setError(
            `Rule reactivate failed (HTTP ${status}): ${data?.error || data?.message || 'unknown'}`,
          );
          return;
        }
        await loadContracts();
        if (typeof rule.contract_id === 'number') {
          broadcastContractsChanged('reactivate', rule.contract_id);
        }
      } catch (e: any) {
        setError(e?.message || 'Rule reactivate failed');
      } finally {
        setBusyRuleId(null);
      }
    },
    [apiFetch, broadcastContractsChanged, busyRuleId, loadContracts],
  );

  // ── Contract migration handlers ────────────────────────────────────────────
  // Re-points an existing registered contract to a new on-chain address
  // (e.g. when a protocol redeploys). All linked execution rules are
  // preserved automatically since they reference `contract_id` (FK), not
  // `contract_address` — see the "Contract migration" doc-comment above
  // the type defs.

  const handleStartMigration = useCallback(
    (c: RegisteredContract) => {
      // Pre-fill the new-address input from the static deprecation registry
      // when we recognise the current address. Nothing is committed yet —
      // user still has to run discovery + confirm.
      const suggestion = findDeprecationSuggestion(c.contract_address, c.network);
      setMigration({
        contractId:   c.id,
        newAddress:   suggestion?.newAddress ?? '',
        step:         'input',
        oldFunctions: normaliseFunctions(c.discovered_functions),
      });
      // Auto-expand the card so the linked-rules list is visible while the
      // user thinks about whether the migration is safe for those rules.
      setExpandedId(c.id);
      setError(null);
    },
    [],
  );

  const handleCancelMigration = useCallback(() => {
    setMigration(null);
  }, []);

  const handleRunDiscovery = useCallback(
    async (c: RegisteredContract) => {
      // Defensive: ignore if the migration session has been swapped to
      // another card under us (shouldn't happen, but cheap to check).
      if (!migration || migration.contractId !== c.id) return;

      const newAddr = migration.newAddress.trim();
      if (!/^C[A-Z0-9]{55}$/.test(newAddr)) {
        setMigration({
          ...migration,
          error: 'Invalid contract address. Stellar contract addresses are 56 characters and start with C.',
        });
        return;
      }
      if (newAddr === c.contract_address) {
        setMigration({
          ...migration,
          error: 'New address is the same as the current address — nothing to migrate.',
        });
        return;
      }

      setMigration({ ...migration, step: 'discovering', error: undefined });
      try {
        const { status, data } = await apiFetch('/api/contracts/discover', {
          method: 'POST',
          body: JSON.stringify({
            contract_address: newAddr,
            network:          c.network || 'testnet',
          }),
        });
        if (status >= 400 || (!data?.success && !Array.isArray(data?.functions))) {
          throw new Error(data?.error || data?.message || `Discovery failed (HTTP ${status})`);
        }
        // Normalise the discovered functions into the same shape used by
        // `normaliseFunctions` — keeps the diff comparison apples-to-apples
        // even if the discover endpoint shifts its parameter shape later.
        const newFunctions: DiscoveredFunction[] = Array.isArray(data?.functions)
          ? data.functions
              .map((f: any): DiscoveredFunction => ({
                name:       typeof f?.name === 'string' ? f.name : '',
                signature:  typeof f?.signature === 'string' ? f.signature : undefined,
                returns:    typeof f?.returns === 'string' ? f.returns : undefined,
                parameters: Array.isArray(f?.parameters)
                  ? f.parameters
                      .map((p: any): DiscoveredFunctionParam => ({
                        name:     typeof p?.name === 'string' ? p.name : '',
                        type:     typeof p?.type === 'string' ? p.type : undefined,
                        optional: !!p?.optional,
                      }))
                      .filter((p: DiscoveredFunctionParam) => p.name)
                  : undefined,
              }))
              .filter((f: DiscoveredFunction) => f.name)
          : [];
        if (newFunctions.length === 0) {
          throw new Error(
            'Discovery returned no functions. Double-check the address and network — ' +
            'an unfunded or wrong-network address will simulate to an empty function list.',
          );
        }
        const oldFunctions =
          migration.oldFunctions ?? normaliseFunctions(c.discovered_functions);
        setMigration({
          ...migration,
          step:         'review',
          newFunctions,
          oldFunctions,
          diffs:        computeFunctionDiff(oldFunctions, newFunctions),
          error:        undefined,
        });
      } catch (e: any) {
        setMigration({
          ...migration,
          step:  'input',
          error: e?.message || 'Discovery failed',
        });
      }
    },
    [migration, apiFetch],
  );

  const handleConfirmMigration = useCallback(
    async (c: RegisteredContract) => {
      if (!migration || migration.contractId !== c.id) return;
      if (migration.step !== 'review' || !migration.newFunctions) return;

      const newAddr = migration.newAddress.trim();
      setMigration({ ...migration, step: 'submitting', error: undefined });

      try {
        // Step 1: PUT /api/contracts/:id with the new address + functions.
        // We forward `function_mappings` verbatim if GeoLink already
        // computed any — that way same-name + same-signature functions
        // keep their parameter mapping without us re-deriving it.
        //
        // Why we then GET to verify: we observed a case where the PUT
        // came back with `{ success: true, contract: {...} }` but the
        // returned row carried the OLD `contract_address`. The success
        // path ran, the migration UI showed "✓ migration complete",
        // but the contract row was unchanged on the server. We now
        // re-fetch the row right after the PUT and compare addresses.
        // If they don't match, the PUT silently dropped the field
        // (likely treated as immutable on the GeoLink side) and we
        // fail loudly instead of pretending success. Console
        // diagnostics in both branches so the actual server payload
        // can be pasted into a hand-off.
        const putBody: Record<string, any> = {
          contract_address:     newAddr,
          discovered_functions: migration.newFunctions,
        };
        if (c.function_mappings) {
          putBody.function_mappings = c.function_mappings;
        }
        const putResp = await apiFetch(`/api/contracts/${c.id}`, {
          method: 'PUT',
          body:   JSON.stringify(putBody),
        });
        if (putResp.status >= 400 || !(putResp.data?.success || putResp.data?.contract)) {
          throw new Error(
            putResp.data?.error ||
              putResp.data?.message ||
              `Update failed (HTTP ${putResp.status})`,
          );
        }

        // Verify the PUT actually persisted the new contract_address.
        // First inspect the echoed row in the PUT response; if the
        // server didn't echo a contract or echoed an old/missing
        // address, do a confirmatory GET. The verification source of
        // truth is the GET response, since some backends echo the
        // request body without applying it.
        let persistedAddress: string | null =
          (putResp.data?.contract?.contract_address as string | undefined) ?? null;
        let verifyResp: { status: number; data: any } | null = null;
        if (!persistedAddress || persistedAddress !== newAddr) {
          verifyResp = await apiFetch(`/api/contracts/${c.id}`);
          persistedAddress =
            (verifyResp.data?.contract?.contract_address as string | undefined) ?? null;
        }

        if (persistedAddress && persistedAddress !== newAddr) {
          // Drop back to review with the immutability flag set —
          // surfaces the "🔁 Re-register at new address" button so
          // the user can use the fallback workflow without leaving
          // this card.
          //
          // NOTE 2026-05-08 evening: GeoLink shipped commit `c88cef5`
          // which makes `contract_address` updatable on PUT (with an
          // audit row in `custom_contracts_repoint_history`). Once
          // Azure picks that up, this verification path should be a
          // "shouldn't happen" branch — but we keep it as a defensive
          // guard against regressions. If you find this firing in the
          // wild, check whether GeoLink's deploy went out and whether
          // the audit row was written. See:
          // `docs/integrations/ai-agents/geolink-put-contract-address-immutable-2026-05-08.md`
          setMigration({
            ...migration,
            step:                 'review',
            error:
              `GeoLink accepted the PUT but the on-chain address is still ` +
              `${persistedAddress}. This GeoLink build treats contract_address ` +
              `as immutable on PUT. Use the re-register fallback below to ` +
              `register ${newAddr} as a fresh contract and recreate the rules ` +
              `under the new id (the old row gets soft-deleted as a zombie).`,
            immutabilityDetected: true,
          });
          return;
        }

        // Step 2: refresh the WASM hash. Best-effort — if this errors we
        // still consider the migration successful because the parent row
        // has been repointed; the user can hit "Fetch from network"
        // manually from the expanded card to retry.
        try {
          const wasmResp = await apiFetch(`/api/contracts/${c.id}/fetch-wasm`, {
            method: 'POST',
            body:   JSON.stringify({ network: c.network || 'testnet' }),
          });
          const hash =
            wasmResp.data?.wasm_hash ||
            wasmResp.data?.hash ||
            wasmResp.data?.contract?.wasm_hash ||
            wasmResp.data?.metadata?.wasm_hash ||
            null;
          if (hash) {
            setWasmHashes((prev) => ({ ...prev, [c.id]: String(hash) }));
          } else {
            // Drop any stale cached hash for this contract — the user
            // should re-fetch since the bytecode just changed.
            setWasmHashes((prev) => {
              const next = { ...prev };
              delete next[c.id];
              return next;
            });
          }
        } catch {
          setWasmHashes((prev) => {
            const next = { ...prev };
            delete next[c.id];
            return next;
          });
        }

        // Drop cached classification too — protocol detection might
        // differ on the new contract (different WASM hash, different
        // public function set).
        setClassifications((prev) => {
          const next = { ...prev };
          delete next[c.id];
          return next;
        });

        setMigration({ ...migration, step: 'success', error: undefined });
        await loadContracts();
        broadcastContractsChanged('migrate', c.id);
        // Auto-collapse the migration UI two seconds after success so the
        // user reads the confirmation, then sees the refreshed card.
        window.setTimeout(() => {
          setMigration((cur) => (cur && cur.contractId === c.id ? null : cur));
        }, 2000);
      } catch (e: any) {
        // Drop back to review (not error) so the diff stays visible and
        // the user can retry without re-running discovery.
        setMigration({
          ...migration,
          step:  'review',
          error: e?.message || 'Migration failed',
        });
      }
    },
    [migration, apiFetch, broadcastContractsChanged, loadContracts],
  );

  /**
   * Re-register fallback for the migration flow. Used when
   * `handleConfirmMigration`'s post-PUT verification finds GeoLink
   * accepted the request but didn't actually update
   * `contract_address` (i.e. the field is immutable on the GeoLink
   * build the user is hitting). Surfaced via a button in the review
   * step when `migration.immutabilityDetected === true`.
   *
   * Workflow (all atomic-ish — partial failure surfaces an error
   * with a list of which steps succeeded so the user can fix
   * manually if needed):
   *
   *   1. POST /api/contracts with `contract_address = newAddress`,
   *      preserving `contract_name` / `function_mappings` / `network`
   *      from the old row and using the discovery results from the
   *      review step. Returns a new `contract.id` (call it
   *      `newContractId`).
   *
   *   2. For each rule under the old contract_id, POST
   *      /api/contracts/rules with the rule's existing fields spread
   *      in and `contract_id` re-pointed to `newContractId`. Strip
   *      server-computed fields (id, contract_name,
   *      contract_address, network, created_at, updated_at) and
   *      ownership fields (apiFetch re-injects on POST). Other
   *      rule parameters (geometry, function name, parameters,
   *      trigger, quorum config, rate limits, etc.) flow verbatim
   *      so the rules behave identically against the new contract.
   *
   *   3. On rule POST success, DELETE /api/contracts/rules/:id on
   *      the old rule so we don't end up with duplicate rules
   *      pointing at both the old and new contracts. The API doc
   *      describes this as a hard delete which is exactly what we
   *      want.
   *
   *   4. DELETE /api/contracts/:id on the old contract to soft-
   *      delete it. The user still has it in their history (with
   *      the orphan/recovery UI we built earlier) but it doesn't
   *      clutter the active list anymore.
   *
   * If any rule fails to recreate, we leave the old rule in place
   * and surface a clear error listing which rules failed plus the
   * new contract id so the user can fix manually.
   *
   * Tracked on the GeoLink side in:
   *   docs/integrations/ai-agents/geolink-put-contract-address-immutable-2026-05-08.md
   */
  const handleRegisterFallback = useCallback(
    async (c: RegisteredContract) => {
      if (!migration || migration.contractId !== c.id) return;
      if (!migration.newFunctions || !migration.newAddress) return;

      const newAddr        = migration.newAddress.trim();
      const linkedRules    = rulesByContract.get(c.id) || [];
      const ownedRulesOnly = linkedRules.filter(
        (r) =>
          !!walletAddress &&
          (r.created_by_public_key === walletAddress ||
            (!r.created_by_public_key && r.owner_public_key === walletAddress)),
      );
      const sharedRulesOnly = linkedRules.filter(
        (r) => !ownedRulesOnly.find((o) => o.id === r.id),
      );

      const lines = [
        'Re-register fallback workflow:',
        '',
        `1. POST a NEW contract row at ${truncate(newAddr, 6, 6)}`,
        `2. Recreate ${ownedRulesOnly.length} owned rule${ownedRulesOnly.length === 1 ? '' : 's'} under the new contract id`,
        `3. DELETE the old owned rule${ownedRulesOnly.length === 1 ? '' : 's'}`,
        `4. Soft-delete the old contract row (it stays in history as a zombie)`,
      ];
      if (sharedRulesOnly.length > 0) {
        lines.push(
          '',
          `Note: ${sharedRulesOnly.length} shared rule${sharedRulesOnly.length === 1 ? '' : 's'} (authored by another wallet) will NOT be migrated — only the wallet that authored them can re-create them. They'll stay attached to the old contract id (which will be soft-deleted) so they'll stop firing until that wallet acts.`,
        );
      }
      lines.push('', 'Continue?');

      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(lines.join('\n'));
      if (!confirmed) return;

      setMigration({
        ...migration,
        step:                 'submitting',
        error:                undefined,
        immutabilityDetected: false,
      });

      try {
        // Step 1: POST new contract row.
        const postBody: Record<string, any> = {
          contract_address:     newAddr,
          contract_name:        c.contract_name || `Contract #${c.id} (migrated)`,
          network:              c.network || 'testnet',
          discovered_functions: migration.newFunctions,
          is_active:            true,
        };
        if (c.function_mappings) postBody.function_mappings = c.function_mappings;
        if (c.description)       postBody.description       = c.description;

        const postResp = await apiFetch('/api/contracts', {
          method: 'POST',
          body:   JSON.stringify(postBody),
        });
        if (postResp.status >= 400 || !(postResp.data?.success || postResp.data?.contract)) {
          throw new Error(
            `New contract registration failed (HTTP ${postResp.status}): ` +
              (postResp.data?.error || postResp.data?.message || 'unknown'),
          );
        }

        const newContractRow = postResp.data?.contract;
        const newContractId: number | undefined =
          typeof newContractRow?.id === 'number' ? newContractRow.id : undefined;
        if (typeof newContractId !== 'number') {
          throw new Error(
            `New contract created but the server didn't return a contract.id — ` +
              `cannot re-parent rules safely. Check the new contract list manually.`,
          );
        }

        // Step 2 + 3: clone each owned rule under the new contract,
        // then DELETE the old rule on success.
        const ruleResults: Array<{
          original: LinkedRule;
          status: 'created' | 'create_failed' | 'delete_failed';
          error?: string;
        }> = [];

        for (const rule of ownedRulesOnly) {
          // Strip server-computed and ownership fields. apiFetch
          // re-injects `created_by_public_key` / `user_public_key`
          // for the connected wallet on POST.
          const cloneBody: Record<string, any> = { ...(rule as Record<string, any>) };
          delete cloneBody.id;
          delete cloneBody.contract_name;
          delete cloneBody.contract_address;
          delete cloneBody.network;
          delete cloneBody.created_at;
          delete cloneBody.updated_at;
          delete cloneBody.created_by_public_key;
          delete cloneBody.owner_public_key;
          cloneBody.contract_id = newContractId;

          let createdOk = false;
          try {
            const ruleResp = await apiFetch('/api/contracts/rules', {
              method: 'POST',
              body:   JSON.stringify(cloneBody),
            });
            if (ruleResp.status >= 400 || !(ruleResp.data?.success || ruleResp.data?.rule)) {
              ruleResults.push({
                original: rule,
                status:   'create_failed',
                error:    ruleResp.data?.error || ruleResp.data?.message || `HTTP ${ruleResp.status}`,
              });
              continue;
            }
            createdOk = true;
          } catch (cloneErr: any) {
            ruleResults.push({
              original: rule,
              status:   'create_failed',
              error:    cloneErr?.message || 'unknown',
            });
            continue;
          }

          if (!createdOk) continue;

          // Old rule DELETE — non-fatal, but we surface it so the
          // user knows there's a duplicate to clean up.
          try {
            const delResp = await apiFetch(`/api/contracts/rules/${rule.id}`, {
              method: 'DELETE',
            });
            if (delResp.status >= 400) {
              ruleResults.push({
                original: rule,
                status:   'delete_failed',
                error:    delResp.data?.error || `HTTP ${delResp.status}`,
              });
            } else {
              ruleResults.push({ original: rule, status: 'created' });
            }
          } catch (delErr: any) {
            ruleResults.push({
              original: rule,
              status:   'delete_failed',
              error:    delErr?.message || 'delete exception',
            });
          }
        }

        // Step 4: soft-delete the old contract row. Best-effort —
        // failure here is annoying but doesn't break the new
        // contract / rules.
        try {
          await apiFetch(`/api/contracts/${c.id}`, { method: 'DELETE' });
        } catch (delContractErr) {
        }

        const created      = ruleResults.filter((r) => r.status === 'created');
        const createFailed = ruleResults.filter((r) => r.status === 'create_failed');
        const deleteFailed = ruleResults.filter((r) => r.status === 'delete_failed');

        await loadContracts();
        broadcastContractsChanged('migrate', c.id);
        broadcastContractsChanged('migrate', newContractId);

        if (createFailed.length > 0 || deleteFailed.length > 0) {
          const summary = [
            `Re-register partially completed.`,
            `New contract id: ${newContractId} (${truncate(newAddr, 6, 6)}).`,
            `${created.length}/${ownedRulesOnly.length} rule${ownedRulesOnly.length === 1 ? '' : 's'} migrated cleanly.`,
          ];
          if (createFailed.length > 0) {
            summary.push(
              `Failed to recreate: ${createFailed.map((r) => `${r.original.rule_name} (${r.error})`).join('; ')}.`,
            );
          }
          if (deleteFailed.length > 0) {
            summary.push(
              `Recreated but couldn't delete the old copy of: ${deleteFailed.map((r) => r.original.rule_name).join(', ')}. You'll have duplicates until you delete them manually.`,
            );
          }
          setMigration({
            ...migration,
            step:                 'review',
            error:                summary.join(' '),
            newContractId,
            immutabilityDetected: false,
          });
          return;
        }

        setMigration({
          ...migration,
          step:                 'success',
          error:                undefined,
          newContractId,
          immutabilityDetected: false,
        });
        // Auto-collapse the migration UI two seconds after success.
        window.setTimeout(() => {
          setMigration((cur) => (cur && cur.contractId === c.id ? null : cur));
        }, 2500);
      } catch (e: any) {
        setMigration({
          ...migration,
          step:  'review',
          error: e?.message || 'Re-register fallback failed',
        });
      }
    },
    [
      migration,
      apiFetch,
      broadcastContractsChanged,
      loadContracts,
      rulesByContract,
      walletAddress,
    ],
  );

  /**
   * Simulate a discovered function via GeoLink's `/test-function`
   * endpoint. This is a dry-run — no signing, no ledger submission, no
   * fee — so it's safe for read-only (`view`) functions and harmless for
   * write functions (the simulator just shows the would-be result and
   * any auth requirements).
   *
   * Param coercion: the user types into plain text inputs. We feed each
   * value through `coerceParamValue()` so `123`, `true`, `["a"]`,
   * `{"x":1}`, `GAB...`, etc. all land as the right JSON shape without
   * forcing the user to remember when to wrap things in quotes.
   */
  const handleSimulate = useCallback(
    async (c: RegisteredContract, fn: DiscoveredFunction) => {
      const key = `${c.id}:${fn.name}`;
      if (simBusyKey === key) return;

      const inputs = simParams[key] || {};
      const params: Record<string, unknown> = {};
      for (const p of fn.parameters || []) {
        const raw = inputs[p.name];
        if (raw === undefined || raw === '') {
          if (p.optional) continue;
          // Send empty string for missing required params; the simulator
          // will surface a precise error rather than us guessing a default.
          params[p.name] = '';
        } else {
          params[p.name] = coerceParamValue(raw);
        }
      }

      setSimBusyKey(key);
      try {
        // Body fields cover three GeoLink server variants we've seen:
        //   - `function_name` + `parameters` (current spec)
        //   - `user_public_key` (caller identity)
        //   - `created_by_public_key` (multi-tenant ownership lookup
        //     fallback — apiFetch injects this too, but explicit is fine)
        // contract_address is included so the server can resolve by
        // address if its lookup-by-id path isn't multi-tenant aware yet.
        const { status, data } = await apiFetch(`/api/contracts/${c.id}/test-function`, {
          method: 'POST',
          body: JSON.stringify({
            function_name: fn.name,
            parameters: params,
            user_public_key: walletAddress || undefined,
            contract_address: c.contract_address,
            network: c.network || 'testnet',
          }),
        });
        // GeoLink's current response shape (post §10 decoder fix):
        //   { success, test_result: { function_name, contract_address,
        //     simulation: { success, result, decoded_result, return_type,
        //                    declared_return_type, inferred_return_type,
        //                    retval_scv_type, retval_xdr, decode_error,
        //                    cost, transaction_data } } }
        // The legacy shape on older deploys was { success, result }.
        // We accept both so the panel works against either build.
        const sim = data?.test_result?.simulation ?? data?.simulation ?? null;
        const simSuccess = sim ? sim.success !== false : false;
        const ok = status < 400 && !!data?.success && simSuccess;

        // Prefer the decoded native value where available; the
        // `result` field is now the same decoded value as a string
        // per the §10 spec, but `decoded_result` keeps type fidelity
        // (BigInt-as-string, native arrays/objects). We render
        // whichever the server provides, falling back through the
        // legacy paths.
        const rawValue =
          sim?.decoded_result !== undefined
            ? sim.decoded_result
            : sim?.result !== undefined
              ? sim.result
              : data?.result !== undefined
                ? data.result
                : data?.returnValue;

        const returnType: string | null =
          sim?.return_type ??
          sim?.inferred_return_type ??
          sim?.declared_return_type ??
          null;

        let payload: string;
        if (ok) {
          // Genuine `()` write-function returns come back as
          // result === "void" with decoded_result === null per §10.
          // Render that as a clearer "() — no return value" line so
          // the user understands a successful simulation with no
          // value isn't an error.
          const isVoid =
            rawValue === null ||
            rawValue === undefined ||
            (typeof rawValue === 'string' && rawValue === 'void');
          payload = isVoid
            ? '() — no return value (write function or void)'
            : formatSimulationResult(rawValue);

          if (sim?.decode_error) {
            payload += `\n\n⚠ decode_error: ${formatSimulationResult(sim.decode_error)}`;
          }
        } else {
          // Failure path: prefer simulation-level error, fall back to
          // the top-level envelope. Multi-tenant 404s are still
          // possible on stale deploys — keep the diagnostic hint.
          const serverErr =
            sim?.error ||
            sim?.decode_error ||
            data?.error ||
            data?.message ||
            data?.details ||
            data;
          const parts: string[] = [`HTTP ${status}`];
          if (serverErr) parts.push(formatSimulationResult(serverErr));
          if (status === 404 && /not found/i.test(String(serverErr))) {
            parts.push('');
            parts.push(
              "GeoLink couldn't resolve this contract for the simulation. " +
                'On older deploys this meant the multi-tenant identity ' +
                "wasn't being honored on /test-function; the canonical " +
                'fix shipped in §10 of the simulator decoder note. If ' +
                'this persists, double-check the deploy matches that spec.',
            );
          }
          payload = parts.join('\n');
        }
        setSimResults((prev) => ({
          ...prev,
          [key]: { ok, payload, ranAt: Date.now(), returnType },
        }));
      } catch (e: any) {
        setSimResults((prev) => ({
          ...prev,
          [key]: { ok: false, payload: e?.message || 'Network error', ranAt: Date.now() },
        }));
      } finally {
        setSimBusyKey(null);
      }
    },
    [apiFetch, simBusyKey, simParams, walletAddress],
  );

  const handleCopy = useCallback(async (address: string) => {
    try {
      await navigator.clipboard.writeText(address);
      setCopiedAddress(address);
      window.setTimeout(() => setCopiedAddress((cur) => (cur === address ? null : cur)), 1200);
    } catch {
      /* clipboard unavailable — silent */
    }
  }, []);

  // Sort: most recent first, then own contracts before shared.
  /**
   * Count of soft-deleted contracts in the raw fetch — used to decide
   * whether to surface the "Show inactive" toggle (we hide it entirely
   * when there are no inactive rows so the header isn't cluttered).
   */
  const inactiveCount = useMemo(
    () => contracts.filter((c) => c.is_active === false).length,
    [contracts],
  );

  /**
   * Render set: filter inactive contracts based on `showInactive`, then
   * sort by ownership (own first) and recency. Inactive cards always
   * appear after active ones within their own ownership bucket so the
   * "live" contracts stay at the top.
   */
  const sortedContracts = useMemo(() => {
    const filtered = showInactive
      ? contracts
      : contracts.filter((c) => c.is_active !== false);
    return [...filtered].sort((a, b) => {
      const ownDelta = Number(isOwn(b)) - Number(isOwn(a));
      if (ownDelta !== 0) return ownDelta;
      // Active above inactive within the same ownership bucket.
      const activeDelta =
        Number(b.is_active !== false) - Number(a.is_active !== false);
      if (activeDelta !== 0) return activeDelta;
      const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
      return tb - ta;
    });
  }, [contracts, isOwn, showInactive]);

  if (!walletAddress) {
    return (
      <div style={styles.notice}>
        ℹ Connect a wallet to see contracts you've registered with GeoLink.
      </div>
    );
  }

  return (
    <div style={styles.wrap}>
      <style>{`
        @keyframes rcl-spin { to { transform: rotate(360deg); } }
        .rcl-spinner { display: inline-block; width: 12px; height: 12px;
          border: 2px solid ${C.border}; border-top-color: ${C.goldDeep};
          border-radius: 50%; animation: rcl-spin 0.7s linear infinite; }
        .rcl-link { color: ${C.blue}; text-decoration: none; }
        .rcl-link:hover { text-decoration: underline; }
        .rcl-btn { transition: background 0.15s, border-color 0.15s; }
        .rcl-btn:hover:not(:disabled) { background: ${C.borderFaint}; }
      `}</style>

      <div style={styles.header}>
        <div>
          <div style={styles.title}>Registered Contracts</div>
          <div style={styles.subtitle}>
            Soroban contracts you've registered with GeoLink. Functions, classification and WASM hash on demand.
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {inactiveCount > 0 && (
            <label
              style={styles.showInactiveToggle}
              title="Soft-deleted (deactivated) contracts are kept on GeoLink and can be reactivated. Toggle this off to hide them from the list."
            >
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                style={{ margin: 0 }}
              />
              Show inactive ({inactiveCount})
            </label>
          )}
          <button
            style={styles.refreshBtn}
            className="rcl-btn"
            onClick={loadContracts}
            disabled={loading}
            title="Refresh from GeoLink"
          >
            {loading ? <span className="rcl-spinner" /> : '↻'} Refresh
          </button>
        </div>
      </div>

      {error && (
        <div style={styles.errorBanner}>
          ⚠ {error}
        </div>
      )}

      {orphanCount > 0 && (
        <div style={styles.orphanBanner}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>
            ℹ {orphanCount} inactive contract{orphanCount === 1 ? '' : 's'} recovered from rule data
          </div>
          <div style={{ fontSize: 11, color: C.textMuted, lineHeight: 1.5 }}>
            This GeoLink deployment is on an older build that doesn't honor{' '}
            <code style={styles.codeInline}>?is_active=all</code> on{' '}
            <code style={styles.codeInline}>GET /api/contracts</code>. We rebuilt the
            missing contract{orphanCount === 1 ? '' : 's'} from your still-existing
            execution rules so you can reactivate{' '}
            {orphanCount === 1 ? 'it' : 'them'} from the list below (look for the green{' '}
            <strong>♻ Reactivate</strong> button). Some metadata (description, WASM
            hash, function inventory) will populate after reactivation — click{' '}
            <strong>Fetch from network</strong> and <strong>Classify</strong> on the
            expanded card once it's active again.
          </div>
        </div>
      )}

      {!loading && sortedContracts.length === 0 && (
        <div style={styles.emptyState}>
          No registered contracts yet.
          <br />
          <span style={{ color: C.textFaint, fontSize: 12 }}>
            Register a contract from the <strong>Execution Rules</strong> tab — it'll show up here with metadata.
          </span>
        </div>
      )}

      <div style={styles.list}>
        {sortedContracts.map((c) => {
          const owned = isOwn(c);
          const fns = normaliseFunctions(c.discovered_functions);
          const isExpanded = expandedId === c.id;
          const wasmHash = wasmHashes[c.id] || c.wasm_hash;
          const classification = classifications[c.id];
          const networkLabel = (c.network || 'testnet').toLowerCase();
          const linkedRules = rulesByContract.get(c.id) || [];
          const activeRules = linkedRules.filter((r) => r.is_active !== false);

          return (
            <div key={c.id} style={styles.card}>
              {/* Header row — name, chips, row actions */}
              <div style={styles.cardHeader}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={styles.contractName}>
                    {c.contract_name || `Contract #${c.id}`}
                    {!c.is_active && <span style={{ ...styles.chip, ...styles.chipMuted }}>inactive</span>}
                  </div>
                  <div style={styles.chipRow}>
                    <span style={{ ...styles.chip, ...chipNetworkStyle(networkLabel) }}>{networkLabel}</span>
                    <span style={{ ...styles.chip, ...styles.chipMuted }}>
                      {fns.length} function{fns.length === 1 ? '' : 's'}
                    </span>
                    {/*
                      Relational chip — surfaces how many execution rules
                      reference this contract and how many are armed. The
                      chip lights up gold when there are active rules so
                      the user can spot "live" contracts at a glance.
                    */}
                    <span
                      style={{
                        ...styles.chip,
                        ...(activeRules.length > 0 ? styles.chipRulesActive : styles.chipMuted),
                      }}
                      title={
                        linkedRules.length === 0
                          ? 'No execution rules reference this contract'
                          : `${linkedRules.length} rule${linkedRules.length === 1 ? '' : 's'} reference this contract (${activeRules.length} active)`
                      }
                    >
                      ⚡ {linkedRules.length} rule{linkedRules.length === 1 ? '' : 's'}
                      {linkedRules.length > 0 && ` · ${activeRules.length} active`}
                    </span>
                    {/*
                      Labelled `creator (legacy)` rather than plain "owned".
                      Everywhere else in the app authority now comes from the
                      contracts; here it cannot, because a GeoLink contract
                      registration has no on-chain record. Saying so is the
                      point — an unlabelled creator check looks like the
                      others and is not.
                    */}
                    {owned ? (
                      <span
                        style={{ ...styles.chip, ...styles.chipOwned }}
                        title={
                          `Authority source: ${AUTHORITY_SOURCE}. Contract registrations `
                          + 'live only in GeoLink, so this is decided by which wallet '
                          + 'registered the row rather than by an on-chain permission.'
                        }
                      >
                        ★ creator (legacy)
                      </span>
                    ) : (
                      <span
                        style={{ ...styles.chip, ...styles.chipShared }}
                        title={
                          c.created_by_public_key
                            ? `Registered by ${truncate(c.created_by_public_key)}`
                            : 'No registering wallet is recorded, so no authority can be established.'
                        }
                      >
                        🌐 shared
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
                  <button
                    style={isExpanded ? styles.detailsBtnActive : styles.detailsBtn}
                    className="rcl-btn"
                    onClick={() => setExpandedId(isExpanded ? null : c.id)}
                    title={isExpanded ? 'Hide advanced details' : 'Show advanced details'}
                  >
                    {isExpanded ? '▴ Hide details' : '▾ Advanced details'}
                  </button>
                  {owned && (
                    <button
                      style={
                        migration?.contractId === c.id
                          ? styles.migrateBtnActive
                          : styles.migrateBtn
                      }
                      className="rcl-btn"
                      onClick={() => {
                        if (migration?.contractId === c.id) {
                          handleCancelMigration();
                        } else {
                          handleStartMigration(c);
                        }
                      }}
                      disabled={
                        busyId === c.id ||
                        (migration !== null && migration.contractId !== c.id)
                      }
                      title={
                        migration?.contractId === c.id
                          ? 'Close migration panel'
                          : 'Migrate this contract to a new on-chain address'
                      }
                    >
                      🔀
                    </button>
                  )}
                  {owned && c.is_active === false && (
                    <button
                      style={styles.reactivateBtn}
                      className="rcl-btn"
                      onClick={() => handleReactivate(c)}
                      disabled={busyId === c.id || migration !== null}
                      title="Reactivate this contract (sets is_active = true)"
                    >
                      {busyId === c.id ? <span className="rcl-spinner" /> : '♻'} Reactivate
                    </button>
                  )}
                  {owned && c.is_active !== false && (
                    <button
                      style={styles.dangerBtn}
                      className="rcl-btn"
                      onClick={() => handleDelete(c)}
                      disabled={busyId === c.id || migration !== null}
                      title="Deactivate (soft delete)"
                    >
                      🗑
                    </button>
                  )}
                </div>
              </div>

              {/* Address row — always visible, primary identifier */}
              <div style={styles.kvRow}>
                <span style={styles.kvLabel}>Address</span>
                <span style={styles.kvValue}>
                  <code style={styles.code}>{truncate(c.contract_address, 8, 6)}</code>
                  <button
                    style={styles.linkBtn}
                    className="rcl-btn"
                    onClick={() => handleCopy(c.contract_address)}
                    title="Copy full address"
                  >
                    {copiedAddress === c.contract_address ? '✓ copied' : '📋 copy'}
                  </button>
                  <a
                    className="rcl-link"
                    href={STELLAR_EXPERT(networkLabel, c.contract_address)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: 11 }}
                  >
                    Stellar Expert ↗
                  </a>
                </span>
              </div>

              {/* Quick metadata always visible — registered/updated dates */}
              <div style={styles.metaGrid}>
                <div style={styles.metaCell}>
                  <div style={styles.metaLabel}>Registered</div>
                  <div style={styles.metaValue}>{fmtDate(c.created_at)}</div>
                </div>
                <div style={styles.metaCell}>
                  <div style={styles.metaLabel}>Last updated</div>
                  <div style={styles.metaValue}>{fmtDate(c.updated_at)}</div>
                </div>
                <div style={styles.metaCell}>
                  <div style={styles.metaLabel}>Status</div>
                  <div style={{ ...styles.metaValue, color: c.is_active === false ? C.red : C.green }}>
                    {c.is_active === false ? '○ inactive' : '● active'}
                  </div>
                </div>
              </div>

              {/*
                Migration panel — shown only for the card whose owner has
                clicked the 🔀 Migrate button. Walks through input →
                discovering → review → submitting → success. Auto-clears
                two seconds after success so the user sees the refreshed
                card metadata next.
              */}
              {migration?.contractId === c.id && (() => {
                const m = migration;
                const suggestion = findDeprecationSuggestion(c.contract_address, c.network);
                const linkedRules = rulesByContract.get(c.id) || [];

                // Pre-compute the rule-impact lists for the review step.
                const removedNames = new Set(
                  (m.diffs || []).filter((d) => d.kind === 'removed').map((d) => d.name),
                );
                const changedNames = new Set(
                  (m.diffs || []).filter((d) => d.kind === 'changed').map((d) => d.name),
                );
                const brokenRules = linkedRules.filter(
                  (r) => r.function_name && removedNames.has(r.function_name),
                );
                const reviewRules = linkedRules.filter(
                  (r) => r.function_name && changedNames.has(r.function_name),
                );

                const counts = {
                  unchanged: (m.diffs || []).filter((d) => d.kind === 'unchanged').length,
                  changed:   (m.diffs || []).filter((d) => d.kind === 'changed').length,
                  removed:   (m.diffs || []).filter((d) => d.kind === 'removed').length,
                  added:     (m.diffs || []).filter((d) => d.kind === 'added').length,
                };

                const isBusy = m.step === 'discovering' || m.step === 'submitting';

                return (
                  <div style={styles.migratePanel}>
                    <div style={styles.migrateHeader}>
                      <span>🔀 Migrate to a new contract address</span>
                      {!isBusy && m.step !== 'success' && (
                        <button
                          style={styles.linkBtn}
                          className="rcl-btn"
                          onClick={handleCancelMigration}
                          title="Close migration panel"
                        >
                          ✕ Cancel
                        </button>
                      )}
                    </div>

                    {m.error && (
                      <div style={styles.migrateError}>⚠ {m.error}</div>
                    )}

                    {m.step === 'input' && (
                      <>
                        {suggestion && (
                          <div style={styles.suggestionBanner}>
                            <div style={{ fontWeight: 700, color: C.goldDeep }}>
                              Known deprecated contract: {suggestion.label}
                            </div>
                            <div style={{ marginTop: 4, fontSize: 11, color: C.textMuted, lineHeight: 1.4 }}>
                              {suggestion.reason}
                            </div>
                            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <code style={styles.code} title={suggestion.newAddress}>
                                {truncate(suggestion.newAddress, 8, 6)}
                              </code>
                              <button
                                style={styles.smallBtn}
                                className="rcl-btn"
                                onClick={() =>
                                  setMigration((cur) =>
                                    cur && cur.contractId === c.id
                                      ? { ...cur, newAddress: suggestion.newAddress, error: undefined }
                                      : cur,
                                  )
                                }
                                title="Pre-fill the new-address input"
                              >
                                ↘ Use suggested address
                              </button>
                            </div>
                          </div>
                        )}

                        <div style={styles.migrateInputRow}>
                          <label style={styles.migrateInputLabel}>
                            New contract address
                            <span style={styles.migrateInputHint}>
                              {(c.network || 'testnet').toUpperCase()} · 56-character C-address
                            </span>
                          </label>
                          <input
                            type="text"
                            style={styles.migrateInput}
                            placeholder="C…"
                            value={m.newAddress}
                            onChange={(e) =>
                              setMigration((cur) =>
                                cur && cur.contractId === c.id
                                  ? { ...cur, newAddress: e.target.value, error: undefined }
                                  : cur,
                              )
                            }
                            spellCheck={false}
                            autoCapitalize="characters"
                          />
                        </div>

                        <div style={styles.migrateActions}>
                          <button
                            style={styles.migrateConfirmBtn}
                            className="rcl-btn"
                            onClick={() => handleRunDiscovery(c)}
                            disabled={!m.newAddress.trim()}
                          >
                            ▶ Run discovery
                          </button>
                          <span style={styles.simHint}>
                            We'll discover the new contract's functions and show you a diff
                            before anything is changed.
                          </span>
                        </div>
                      </>
                    )}

                    {m.step === 'discovering' && (
                      <div style={styles.migrateStatus}>
                        <span className="rcl-spinner" />{' '}
                        Discovering functions on{' '}
                        <code style={styles.code}>{truncate(m.newAddress.trim(), 6, 4)}</code>…
                      </div>
                    )}

                    {m.step === 'review' && m.diffs && (
                      <>
                        <div style={styles.migrateAddrSummary}>
                          <span style={styles.kvLabel}>From</span>
                          <code style={{ ...styles.code, color: C.textMuted }}>
                            {truncate(c.contract_address, 6, 4)}
                          </code>
                          <span style={{ color: C.textFaint }}>→</span>
                          <span style={styles.kvLabel}>To</span>
                          <code style={{ ...styles.code, color: C.goldDeep, fontWeight: 700 }}>
                            {truncate(m.newAddress.trim(), 6, 4)}
                          </code>
                        </div>

                        <div style={styles.diffSummary}>
                          <span style={{ ...styles.chip, ...styles.chipDiffOk }}>
                            ✓ {counts.unchanged} unchanged
                          </span>
                          {counts.changed > 0 && (
                            <span style={{ ...styles.chip, ...styles.chipDiffWarn }}>
                              ⚠ {counts.changed} signature drift
                            </span>
                          )}
                          {counts.removed > 0 && (
                            <span style={{ ...styles.chip, ...styles.chipDiffErr }}>
                              ✗ {counts.removed} removed
                            </span>
                          )}
                          {counts.added > 0 && (
                            <span style={{ ...styles.chip, ...styles.chipDiffNew }}>
                              + {counts.added} new
                            </span>
                          )}
                        </div>

                        {(brokenRules.length > 0 || reviewRules.length > 0) && (
                          <div style={styles.ruleImpactBox}>
                            <div style={{ fontWeight: 700, marginBottom: 4 }}>
                              ⚠ Linked execution rule impact
                            </div>
                            {brokenRules.length > 0 && (
                              <>
                                <div style={{ color: C.red, fontSize: 11, marginBottom: 2 }}>
                                  {brokenRules.length} rule{brokenRules.length === 1 ? '' : 's'} call
                                  {brokenRules.length === 1 ? 's' : ''} a function that won't exist on
                                  the new contract — they'll need editing or deactivation after
                                  migration:
                                </div>
                                <ul style={styles.ruleImpactList}>
                                  {brokenRules.map((r) => (
                                    <li key={`b-${r.id}`}>
                                      <strong>{r.rule_name}</strong>{' '}
                                      <code style={styles.code}>{r.function_name}()</code>
                                    </li>
                                  ))}
                                </ul>
                              </>
                            )}
                            {reviewRules.length > 0 && (
                              <>
                                <div
                                  style={{
                                    color:        C.goldDeep,
                                    fontSize:     11,
                                    marginTop:    brokenRules.length ? 8 : 0,
                                    marginBottom: 2,
                                  }}
                                >
                                  {reviewRules.length} rule{reviewRules.length === 1 ? '' : 's'} call
                                  {reviewRules.length === 1 ? 's' : ''} a function whose signature
                                  changed — review parameters after migration:
                                </div>
                                <ul style={styles.ruleImpactList}>
                                  {reviewRules.map((r) => (
                                    <li key={`r-${r.id}`}>
                                      <strong>{r.rule_name}</strong>{' '}
                                      <code style={styles.code}>{r.function_name}()</code>
                                    </li>
                                  ))}
                                </ul>
                              </>
                            )}
                          </div>
                        )}

                        <div style={styles.diffList}>
                          {m.diffs.map((d) => {
                            const color =
                              d.kind === 'unchanged' ? C.green :
                              d.kind === 'changed'   ? C.goldDeep :
                              d.kind === 'removed'   ? C.red :
                                                       C.blue;
                            return (
                              <div key={`${d.kind}:${d.name}`} style={styles.diffRow}>
                                <span
                                  style={{
                                    color,
                                    fontWeight: 700,
                                    minWidth:   16,
                                    textAlign:  'center',
                                    flexShrink: 0,
                                  }}
                                >
                                  {diffIcon(d.kind)}
                                </span>
                                <code
                                  style={{ ...styles.code, color: C.text, fontWeight: 600 }}
                                >
                                  {d.name}
                                </code>
                                {d.kind === 'unchanged' && d.oldSignature && (
                                  <span style={styles.fnSignature}>{d.oldSignature}</span>
                                )}
                                {d.kind === 'changed' && (
                                  <div style={styles.diffSigBlock}>
                                    <div>
                                      <span style={{ color: C.red, fontWeight: 700 }}>−</span>{' '}
                                      <span style={{ color: C.textMuted }}>{d.oldSignature}</span>
                                    </div>
                                    <div>
                                      <span style={{ color: C.green, fontWeight: 700 }}>+</span>{' '}
                                      <span style={{ color: C.text }}>{d.newSignature}</span>
                                    </div>
                                  </div>
                                )}
                                {d.kind === 'removed' && d.oldSignature && (
                                  <span
                                    style={{
                                      ...styles.fnSignature,
                                      color:          C.red,
                                      textDecoration: 'line-through',
                                    }}
                                  >
                                    {d.oldSignature}
                                  </span>
                                )}
                                {d.kind === 'added' && d.newSignature && (
                                  <span style={{ ...styles.fnSignature, color: C.blue }}>
                                    {d.newSignature}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        <div style={styles.migrateActions}>
                          <button
                            style={styles.migrateConfirmBtn}
                            className="rcl-btn"
                            onClick={() => handleConfirmMigration(c)}
                          >
                            ✓ Confirm migration
                          </button>
                          <button
                            style={styles.smallBtn}
                            className="rcl-btn"
                            onClick={() =>
                              setMigration((cur) =>
                                cur && cur.contractId === c.id
                                  ? { ...cur, step: 'input', error: undefined }
                                  : cur,
                              )
                            }
                          >
                            ← Edit address
                          </button>
                          {brokenRules.length > 0 ? (
                            <span style={{ ...styles.simHint, color: C.red }}>
                              ⚠ {brokenRules.length} rule
                              {brokenRules.length === 1 ? '' : 's'} will need a follow-up edit.
                            </span>
                          ) : (
                            <span style={styles.simHint}>
                              Linked rules keep their parameters and history — no rule edits
                              required.
                            </span>
                          )}
                        </div>

                        {m.immutabilityDetected && (
                          <div style={styles.fallbackBox}>
                            <div style={styles.fallbackTitle}>
                              🔁 Migrate-in-place isn&apos;t supported on this GeoLink
                              build.
                            </div>
                            <div style={styles.fallbackBody}>
                              GeoLink&apos;s <code style={styles.code}>PUT /api/contracts/:id</code>{' '}
                              accepted the request but didn&apos;t actually change{' '}
                              <code style={styles.code}>contract_address</code>. The
                              workaround is to register a fresh row at the new address
                              and re-parent the rules onto it. Tracked with the GeoLink
                              team in{' '}
                              <code style={styles.code}>
                                geolink-put-contract-address-immutable-2026-05-08.md
                              </code>
                              .
                            </div>
                            <div style={styles.fallbackBody}>
                              <strong>Re-register fallback will:</strong>
                              <ol style={styles.fallbackList}>
                                <li>
                                  POST a new contract row at{' '}
                                  <code style={styles.code}>
                                    {truncate(m.newAddress.trim(), 6, 6)}
                                  </code>
                                </li>
                                <li>
                                  Recreate each rule you own under the new contract id
                                  (geometry, function, params, triggers — all preserved
                                  verbatim)
                                </li>
                                <li>DELETE the old rules</li>
                                <li>Soft-delete the old contract row</li>
                              </ol>
                              The old row stays in your history (recoverable via
                              &quot;Show inactive&quot;) but the new contract gets a new id —
                              anything that hard-coded the old id will need to be
                              updated.
                            </div>
                            <div style={styles.migrateActions}>
                              <button
                                style={styles.fallbackBtn}
                                className="rcl-btn"
                                onClick={() => handleRegisterFallback(c)}
                              >
                                🔁 Re-register at new address
                              </button>
                              <button
                                style={styles.smallBtn}
                                className="rcl-btn"
                                onClick={() =>
                                  setMigration((cur) =>
                                    cur && cur.contractId === c.id
                                      ? { ...cur, immutabilityDetected: false, error: undefined }
                                      : cur,
                                  )
                                }
                              >
                                ✕ Dismiss fallback
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {m.step === 'submitting' && (
                      <div style={styles.migrateStatus}>
                        <span className="rcl-spinner" />{' '}
                        Updating contract row & refreshing WASM hash…
                      </div>
                    )}

                    {m.step === 'success' && (
                      <div style={{ ...styles.migrateStatus, color: C.green, fontWeight: 700 }}>
                        {typeof m.newContractId === 'number' ? (
                          <>
                            ✓ Re-register fallback complete — new contract id{' '}
                            <code style={{ ...styles.code, color: C.green }}>
                              {m.newContractId}
                            </code>{' '}
                            at{' '}
                            <code style={{ ...styles.code, color: C.green }}>
                              {truncate(m.newAddress.trim(), 6, 6)}
                            </code>
                            , {linkedRules.length > 0
                              ? `${linkedRules.length} rule${linkedRules.length === 1 ? '' : 's'} re-parented, `
                              : ''}
                            old row soft-deleted.
                          </>
                        ) : (
                          <>
                            ✓ Migration complete — contract repointed,{' '}
                            {linkedRules.length > 0
                              ? `${linkedRules.length} linked rule${linkedRules.length === 1 ? '' : 's'} preserved, `
                              : ''}
                            WASM hash refreshed.
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              {isExpanded && (
                <div style={styles.expanded}>
                  {c.description && (
                    <div style={styles.kvRow}>
                      <span style={styles.kvLabel}>Description</span>
                      <span style={styles.kvValue}>{c.description}</span>
                    </div>
                  )}

                  {/* WASM hash row — fetch on demand */}
                  <div style={styles.kvRow}>
                    <span style={styles.kvLabel}>WASM hash</span>
                    <span style={styles.kvValue}>
                      {wasmHash ? (
                        <>
                          <code style={styles.code}>{truncate(wasmHash, 12, 8)}</code>
                          <button
                            style={styles.linkBtn}
                            className="rcl-btn"
                            onClick={() => handleCopy(wasmHash)}
                            title="Copy full hash"
                          >
                            {copiedAddress === wasmHash ? '✓ copied' : '📋 copy'}
                          </button>
                        </>
                      ) : (
                        <button
                          style={styles.smallBtn}
                          className="rcl-btn"
                          onClick={() => handleFetchWasm(c)}
                          disabled={busyId === c.id}
                          title="Fetch WASM hash from Stellar via GeoLink"
                        >
                          {busyId === c.id ? <span className="rcl-spinner" /> : '⬇'} Fetch from network
                        </button>
                      )}
                    </span>
                  </div>

                  {/* Classification row — fetch on demand */}
                  <div style={styles.kvRow}>
                    <span style={styles.kvLabel}>Classification</span>
                    <span style={styles.kvValue}>
                      {classification ? (
                        <code style={styles.code}>
                          {classification.classification ||
                            classification.protocol ||
                            (classification.is_defindex ? 'defindex' : 'unclassified')}
                        </code>
                      ) : (
                        <button
                          style={styles.smallBtn}
                          className="rcl-btn"
                          onClick={() => handleClassify(c)}
                          disabled={busyId === c.id}
                          title="Detect protocol / DeFindex membership via GeoLink"
                        >
                          {busyId === c.id ? <span className="rcl-spinner" /> : '🔍'} Classify
                        </button>
                      )}
                    </span>
                  </div>

                  {/* Owner identity (only when shared, since "owned" is implied by the chip) */}
                  {!owned && c.created_by_public_key && (
                    <div style={styles.kvRow}>
                      <span style={styles.kvLabel}>Registered by</span>
                      <span style={styles.kvValue}>
                        <code style={styles.code} title={c.created_by_public_key}>
                          {truncate(c.created_by_public_key)}
                        </code>
                      </span>
                    </div>
                  )}

                  {/*
                    Linked execution rules — read-only relational view.
                    Editing/toggling/deleting rules still happens in the
                    Execution Rules tab so we don't fork that surface.
                    Each row shows: status dot, rule name, function call
                    (the actual contract function this rule invokes),
                    trigger event, and ownership pill.
                  */}
                  {linkedRules.length > 0 && (
                    <div style={styles.functionsBlock}>
                      <div style={styles.functionsHeader}>
                        Linked execution rules ({linkedRules.length}
                        {activeRules.length !== linkedRules.length && ` · ${activeRules.length} active`})
                      </div>
                      <div style={styles.functionsList}>
                        {linkedRules.map((r) => {
                          const ruleOwned =
                            !!walletAddress &&
                            (r.created_by_public_key === walletAddress ||
                              (!r.created_by_public_key && r.owner_public_key === walletAddress));
                          const ruleInactive = r.is_active === false;
                          const canReactivateRule = ruleOwned && ruleInactive;
                          const ruleBusy = busyRuleId === r.id;
                          return (
                            <div key={r.id} style={styles.ruleRow}>
                              <span
                                style={{
                                  ...styles.ruleDot,
                                  background: ruleInactive ? C.textFaint : C.green,
                                }}
                                title={ruleInactive ? 'Inactive' : 'Active'}
                              />
                              <span style={styles.ruleName} title={r.rule_name}>
                                {r.rule_name}
                              </span>
                              {r.function_name && (
                                <code style={{ ...styles.code, color: C.goldDeep }}>{r.function_name}()</code>
                              )}
                              {r.trigger_on && (
                                <span style={styles.fnSignature}>on {r.trigger_on}</span>
                              )}
                              {!ruleOwned && (
                                <span style={{ ...styles.chip, ...styles.chipShared, fontSize: 9 }}>
                                  shared
                                </span>
                              )}
                              {canReactivateRule && (
                                /* GeoLink's DELETE /api/contracts/:id appears to
                                   cascade rule.is_active to false and the parent
                                   reactivate doesn't propagate. This per-rule
                                   undo flips the rule back on without forcing
                                   the user to leave this tab. */
                                <button
                                  style={styles.reactivateRuleBtn}
                                  className="rcl-btn"
                                  onClick={() => handleReactivateRule(r)}
                                  disabled={ruleBusy}
                                  title="Reactivate this rule (was paused by parent contract delete)"
                                >
                                  {ruleBusy ? '…' : '♻'}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/*
                    Discovered functions list with inline simulator.
                    Each function exposes a "▶ Simulate" button that
                    calls GeoLink's `/test-function` (dry-run, no
                    signing). View functions return their value; write
                    functions return what they would do plus auth
                    requirements without committing to ledger.
                  */}
                  {fns.length > 0 && (
                    <div style={styles.functionsBlock}>
                      <div style={styles.functionsHeader}>
                        Discovered functions ({fns.length})
                        <span style={styles.functionsHelp}>
                          Click <strong>Simulate</strong> to dry-run via GeoLink (no signing, no ledger submission).
                        </span>
                      </div>
                      <div style={styles.functionsList}>
                        {fns.map((f, i) => {
                          const simKey = `${c.id}:${f.name}`;
                          const isSimOpen = simOpenKey === simKey;
                          const simResult = simResults[simKey];
                          const isSimBusy = simBusyKey === simKey;
                          const hasParams = !!(f.parameters && f.parameters.length > 0);
                          return (
                            <div key={`${f.name}-${i}`} style={styles.fnBlock}>
                              <div style={styles.fnRow}>
                                <code style={{ ...styles.code, color: C.goldDeep }}>{f.name}</code>
                                {f.signature && (
                                  <span style={styles.fnSignature}>{f.signature}</span>
                                )}
                                {f.returns && (
                                  <span style={styles.fnReturns}>→ {f.returns}</span>
                                )}
                                <span style={{ flex: 1 }} />
                                <button
                                  style={styles.simulateBtn}
                                  className="rcl-btn"
                                  onClick={() => {
                                    if (hasParams) {
                                      setSimOpenKey(isSimOpen ? null : simKey);
                                    } else {
                                      handleSimulate(c, f);
                                    }
                                  }}
                                  disabled={isSimBusy}
                                  title={hasParams ? 'Open parameter form & simulate' : 'Simulate this function (no signing required)'}
                                >
                                  {isSimBusy ? <span className="rcl-spinner" /> : '▶'}{' '}
                                  {hasParams ? (isSimOpen ? 'Cancel' : 'Simulate…') : 'Simulate'}
                                </button>
                              </div>

                              {/* Param entry form — only when the function takes args */}
                              {isSimOpen && hasParams && (
                                <div style={styles.simForm}>
                                  {(f.parameters || []).map((p) => (
                                    <div key={p.name} style={styles.simParamRow}>
                                      <label style={styles.simParamLabel} title={p.type}>
                                        {p.name}
                                        {p.optional && <span style={styles.simOptional}> (optional)</span>}
                                        {p.type && <span style={styles.simType}>: {p.type}</span>}
                                      </label>
                                      <input
                                        type="text"
                                        style={styles.simInput}
                                        placeholder={p.type || 'value'}
                                        value={(simParams[simKey] || {})[p.name] || ''}
                                        onChange={(e) =>
                                          setSimParams((prev) => ({
                                            ...prev,
                                            [simKey]: { ...(prev[simKey] || {}), [p.name]: e.target.value },
                                          }))
                                        }
                                      />
                                    </div>
                                  ))}
                                  <div style={styles.simFormActions}>
                                    <button
                                      style={styles.simulateBtn}
                                      className="rcl-btn"
                                      onClick={() => handleSimulate(c, f)}
                                      disabled={isSimBusy}
                                    >
                                      {isSimBusy ? <span className="rcl-spinner" /> : '▶'} Run simulation
                                    </button>
                                    <span style={styles.simHint}>
                                      Tip: numbers, booleans, JSON arrays/objects are auto-detected. Plain text becomes a string.
                                    </span>
                                  </div>
                                </div>
                              )}

                              {/* Result panel */}
                              {simResult && (
                                <div style={simResult.ok ? styles.simResultOk : styles.simResultErr}>
                                  <div style={styles.simResultHeader}>
                                    {simResult.ok ? '✓ Simulation succeeded' : '✗ Simulation failed'}
                                    {simResult.ok && simResult.returnType && (
                                      <span
                                        style={styles.simReturnTypeChip}
                                        title="Decoded return type (from on-chain ScVal variant when available)"
                                      >
                                        {simResult.returnType}
                                      </span>
                                    )}
                                    <span style={styles.simResultTime}>
                                      {new Date(simResult.ranAt).toLocaleTimeString()}
                                    </span>
                                    <button
                                      style={styles.linkBtn}
                                      className="rcl-btn"
                                      onClick={() => handleCopy(simResult.payload)}
                                      title="Copy result"
                                    >
                                      {copiedAddress === simResult.payload ? '✓ copied' : '📋 copy'}
                                    </button>
                                  </div>
                                  <pre style={styles.simResultPayload}>{simResult.payload}</pre>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────

const chipNetworkStyle = (network: string): React.CSSProperties => ({
  color: network === 'mainnet' ? C.green : C.textMuted,
  background: network === 'mainnet' ? 'rgba(40,167,69,0.08)' : C.cardBg,
  borderColor: network === 'mainnet' ? 'rgba(40,167,69,0.3)' : C.border,
});

const styles: Record<string, React.CSSProperties> = {
  wrap: { padding: '0 2px' },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 16,
  },
  title: { fontSize: 14, fontWeight: 700, color: C.text },
  subtitle: { fontSize: 11, color: C.textFaint, marginTop: 2 },
  refreshBtn: {
    background: C.panelBg,
    border: `1px solid ${C.border}`,
    color: C.textMuted,
    fontSize: 12,
    fontWeight: 600,
    padding: '6px 12px',
    borderRadius: 7,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
  },
  orphanBanner: {
    background: 'rgba(31,111,235,0.06)',
    border: `1px solid rgba(31,111,235,0.3)`,
    borderRadius: 8,
    padding: '10px 13px',
    fontSize: 12,
    color: C.text,
    marginBottom: 12,
  },
  codeInline: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: 11,
    background: C.cardBg,
    padding: '1px 5px',
    borderRadius: 3,
    color: C.text,
  },
  errorBanner: {
    background: 'rgba(239,68,68,0.12)',
    border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: 7,
    padding: '9px 13px',
    fontSize: 12,
    color: C.red,
    marginBottom: 12,
  },
  notice: {
    background: 'rgba(31,111,235,0.08)',
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: '14px 16px',
    fontSize: 13,
    color: C.textMuted,
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 0',
    color: C.textMuted,
    fontSize: 13,
    background: C.cardBg,
    border: `1px solid ${C.borderFaint}`,
    borderRadius: 12,
  },
  list: { display: 'flex', flexDirection: 'column', gap: 12 },
  card: {
    background: C.panelBg,
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    padding: '14px 16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 10,
  },
  contractName: {
    fontSize: 14,
    fontWeight: 700,
    color: C.text,
    marginBottom: 6,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  chipRow: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  chip: {
    fontSize: 10,
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: 999,
    background: C.panelBg,
    border: `1px solid ${C.border}`,
    color: C.textMuted,
    letterSpacing: 0.2,
    textTransform: 'uppercase',
  },
  chipMuted: { color: C.textFaint },
  chipOwned: { color: C.goldDeep, borderColor: C.gold, background: 'rgba(255,215,0,0.08)' },
  chipShared: { color: C.textFaint },
  chipRulesActive: {
    color: C.goldDeep,
    background: 'rgba(255,215,0,0.10)',
    borderColor: C.gold,
  },
  detailsBtn: {
    background: C.panelBg,
    border: `1px solid ${C.border}`,
    color: C.textMuted,
    fontSize: 11,
    fontWeight: 600,
    height: 28,
    padding: '0 11px',
    borderRadius: 6,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  detailsBtnActive: {
    background: 'rgba(255,215,0,0.10)',
    border: `1px solid ${C.gold}`,
    color: C.goldDeep,
    fontSize: 11,
    fontWeight: 700,
    height: 28,
    padding: '0 11px',
    borderRadius: 6,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  dangerBtn: {
    background: C.panelBg,
    border: `1px solid rgba(220,53,69,0.4)`,
    color: C.red,
    fontSize: 13,
    width: 30,
    height: 28,
    borderRadius: 6,
    cursor: 'pointer',
    padding: 0,
  },
  reactivateBtn: {
    background: 'rgba(40,167,69,0.10)',
    border: `1px solid ${C.green}`,
    color: C.green,
    fontSize: 11,
    fontWeight: 700,
    height: 28,
    padding: '0 11px',
    borderRadius: 6,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    whiteSpace: 'nowrap',
  },
  reactivateRuleBtn: {
    background: 'rgba(40,167,69,0.10)',
    border: `1px solid ${C.green}`,
    color: C.green,
    fontSize: 10,
    fontWeight: 700,
    height: 20,
    padding: '0 7px',
    borderRadius: 4,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 22,
    flexShrink: 0,
  },
  showInactiveToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 11,
    fontWeight: 600,
    color: C.textMuted,
    background: C.panelBg,
    border: `1px solid ${C.border}`,
    borderRadius: 7,
    padding: '6px 10px',
    cursor: 'pointer',
    userSelect: 'none',
  },
  smallBtn: {
    background: C.panelBg,
    border: `1px solid ${C.border}`,
    color: C.textMuted,
    fontSize: 11,
    fontWeight: 600,
    padding: '3px 9px',
    borderRadius: 6,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  },
  linkBtn: {
    background: 'transparent',
    border: 'none',
    color: C.blue,
    fontSize: 11,
    cursor: 'pointer',
    padding: 0,
    marginLeft: 4,
  },
  kvRow: {
    display: 'flex',
    fontSize: 12,
    padding: '5px 0',
    borderTop: `1px solid ${C.borderFaint}`,
    gap: 12,
  },
  kvLabel: { color: C.textFaint, minWidth: 110, fontWeight: 600 },
  kvValue: {
    color: C.text,
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    overflowWrap: 'anywhere',
  },
  metaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 8,
    marginTop: 8,
    paddingTop: 8,
    borderTop: `1px solid ${C.borderFaint}`,
  },
  metaCell: {
    background: C.cardBg,
    border: `1px solid ${C.borderFaint}`,
    borderRadius: 6,
    padding: '6px 8px',
  },
  metaLabel: { fontSize: 9, fontWeight: 700, color: C.textFaint, textTransform: 'uppercase', letterSpacing: 0.4 },
  metaValue: { fontSize: 12, color: C.text, marginTop: 2 },
  code: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: 11,
    background: C.cardBg,
    padding: '2px 6px',
    borderRadius: 4,
    color: C.text,
  },
  expanded: { marginTop: 6 },
  functionsBlock: {
    marginTop: 8,
    paddingTop: 10,
    borderTop: `1px solid ${C.borderFaint}`,
  },
  functionsHeader: {
    fontSize: 11,
    fontWeight: 700,
    color: C.textMuted,
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  functionsList: { display: 'flex', flexDirection: 'column', gap: 4 },
  fnRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    fontSize: 12,
  },
  fnSignature: {
    fontSize: 11,
    color: C.textFaint,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  },
  fnReturns: { fontSize: 11, color: C.green, fontWeight: 600 },
  ruleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    fontSize: 12,
    padding: '4px 0',
  },
  ruleDot: {
    display: 'inline-block',
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
  },
  ruleName: {
    fontWeight: 600,
    color: C.text,
    minWidth: 0,
    flexShrink: 1,
  },
  functionsHelp: {
    display: 'block',
    fontSize: 10,
    fontWeight: 400,
    color: C.textFaint,
    textTransform: 'none',
    letterSpacing: 0,
    marginTop: 2,
  },
  fnBlock: {
    paddingBottom: 8,
    borderBottom: `1px dashed ${C.borderFaint}`,
  },
  simulateBtn: {
    background: C.panelBg,
    border: `1px solid ${C.gold}`,
    color: C.goldDeep,
    fontSize: 11,
    fontWeight: 700,
    padding: '4px 10px',
    borderRadius: 6,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    whiteSpace: 'nowrap',
  },
  simForm: {
    background: C.cardBg,
    border: `1px solid ${C.borderFaint}`,
    borderRadius: 6,
    padding: '10px 12px',
    marginTop: 6,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  simParamRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
  },
  simParamLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: C.textMuted,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  },
  simOptional: { color: C.textFaint, fontWeight: 400 },
  simType: { color: C.textFaint, fontWeight: 400 },
  simInput: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: 12,
    padding: '6px 9px',
    border: `1px solid ${C.border}`,
    borderRadius: 5,
    background: C.panelBg,
    color: C.text,
    outline: 'none',
  },
  simFormActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    marginTop: 4,
  },
  simHint: {
    fontSize: 10,
    color: C.textFaint,
    fontStyle: 'italic',
  },
  simResultOk: {
    background: 'rgba(40,167,69,0.06)',
    border: `1px solid rgba(40,167,69,0.3)`,
    borderRadius: 6,
    padding: '8px 11px',
    marginTop: 6,
  },
  simResultErr: {
    background: 'rgba(220,53,69,0.06)',
    border: `1px solid rgba(220,53,69,0.3)`,
    borderRadius: 6,
    padding: '8px 11px',
    marginTop: 6,
  },
  simResultHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 11,
    fontWeight: 700,
    marginBottom: 4,
  },
  simResultTime: {
    fontWeight: 400,
    color: C.textFaint,
    fontSize: 10,
    flex: 1,
  },
  simReturnTypeChip: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: 10,
    fontWeight: 600,
    color: C.goldDeep,
    background: 'rgba(212, 175, 55, 0.12)',
    border: `1px solid ${C.goldDeep}`,
    padding: '1px 6px',
    borderRadius: 4,
    letterSpacing: 0.2,
  },
  simResultPayload: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: 11,
    margin: 0,
    padding: '6px 8px',
    background: C.panelBg,
    border: `1px solid ${C.borderFaint}`,
    borderRadius: 4,
    color: C.text,
    maxHeight: 220,
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  },

  // ── Migration UI ───────────────────────────────────────────────────────────

  migrateBtn: {
    background: C.panelBg,
    border: `1px solid ${C.gold}`,
    color: C.goldDeep,
    fontSize: 13,
    width: 30,
    height: 28,
    borderRadius: 6,
    cursor: 'pointer',
    padding: 0,
  },
  migrateBtnActive: {
    background: 'rgba(255,215,0,0.18)',
    border: `1px solid ${C.goldDeep}`,
    color: C.goldDeep,
    fontSize: 13,
    width: 30,
    height: 28,
    borderRadius: 6,
    cursor: 'pointer',
    padding: 0,
  },
  migratePanel: {
    marginTop: 10,
    padding: '12px 14px',
    background: 'rgba(255,215,0,0.06)',
    border: `1px solid ${C.gold}`,
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  migrateHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: 11,
    fontWeight: 700,
    color: C.goldDeep,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  migrateError: {
    background: 'rgba(220,53,69,0.10)',
    border: `1px solid rgba(220,53,69,0.4)`,
    borderRadius: 6,
    padding: '6px 10px',
    fontSize: 11,
    color: C.red,
    fontWeight: 600,
  },
  fallbackBox: {
    background: 'rgba(255,193,7,0.06)',
    border: `1px solid rgba(255,193,7,0.45)`,
    borderRadius: 8,
    padding: '10px 12px',
    marginTop: 10,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8,
  },
  fallbackTitle: {
    fontSize: 12,
    fontWeight: 800,
    color: C.goldDeep,
    letterSpacing: 0.2,
  },
  fallbackBody: {
    fontSize: 11,
    color: C.textMuted,
    lineHeight: 1.5,
  },
  fallbackList: {
    margin: '4px 0 0 0',
    paddingLeft: 18,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
  },
  fallbackBtn: {
    background: C.goldDeep,
    color: '#fff',
    fontSize: 11,
    fontWeight: 800,
    height: 28,
    padding: '0 12px',
    borderRadius: 6,
    border: `1px solid ${C.goldDeep}`,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    whiteSpace: 'nowrap' as const,
  },
  migrateStatus: {
    fontSize: 12,
    color: C.textMuted,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 0',
    flexWrap: 'wrap',
  },
  migrateActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    marginTop: 2,
  },
  migrateAddrSummary: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    fontSize: 11,
    paddingBottom: 6,
    borderBottom: `1px solid ${C.borderFaint}`,
  },
  migrateInputRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  migrateInputLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: C.textMuted,
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
    flexWrap: 'wrap',
  },
  migrateInputHint: {
    fontSize: 10,
    fontWeight: 400,
    color: C.textFaint,
  },
  migrateInput: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: 12,
    padding: '8px 10px',
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    background: C.panelBg,
    color: C.text,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  migrateConfirmBtn: {
    background: C.gold,
    border: `1px solid ${C.goldDeep}`,
    color: C.goldDeep,
    fontSize: 12,
    fontWeight: 700,
    padding: '6px 14px',
    borderRadius: 6,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    whiteSpace: 'nowrap',
  },
  suggestionBanner: {
    background: 'rgba(255,215,0,0.12)',
    border: `1px dashed ${C.gold}`,
    borderRadius: 6,
    padding: '10px 12px',
    fontSize: 12,
    color: C.text,
  },
  diffSummary: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
  },
  chipDiffOk: {
    color: C.green,
    borderColor: 'rgba(40,167,69,0.4)',
    background: 'rgba(40,167,69,0.10)',
  },
  chipDiffWarn: {
    color: C.goldDeep,
    borderColor: C.gold,
    background: 'rgba(255,215,0,0.14)',
  },
  chipDiffErr: {
    color: C.red,
    borderColor: 'rgba(220,53,69,0.4)',
    background: 'rgba(220,53,69,0.10)',
  },
  chipDiffNew: {
    color: C.blue,
    borderColor: 'rgba(31,111,235,0.4)',
    background: 'rgba(31,111,235,0.10)',
  },
  ruleImpactBox: {
    background: 'rgba(220,53,69,0.06)',
    border: `1px solid rgba(220,53,69,0.3)`,
    borderRadius: 6,
    padding: '8px 10px',
    fontSize: 12,
    color: C.text,
  },
  ruleImpactList: {
    margin: '4px 0 0 18px',
    padding: 0,
    fontSize: 11,
    color: C.textMuted,
    lineHeight: 1.5,
  },
  diffList: {
    background: C.cardBg,
    border: `1px solid ${C.borderFaint}`,
    borderRadius: 6,
    padding: '8px 10px',
    maxHeight: 280,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
  },
  diffRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    fontSize: 11,
    flexWrap: 'wrap',
    paddingBottom: 3,
    borderBottom: `1px dashed ${C.borderFaint}`,
  },
  diffSigBlock: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: 11,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    flex: 1,
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
};
